// English language pack
const messages = {
  // Common
  common: {
    loading: 'Loading...',
    error: 'Error',
    success: 'Success',
    warning: 'Warning',
    confirm: 'Confirm',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    search: 'Search',
    filter: 'Filter',
    refresh: 'Refresh',
    back: 'Back',
    next: 'Next',
    submit: 'Submit',
    reset: 'Reset',
    close: 'Close',
    more: 'More',
    less: 'Less',
    all: 'All',
    none: 'None',
    required: 'Required',
    optional: 'Optional',
    unknown: 'Unknown',
    na: 'N/A',
    switchLanguage: 'Switch language',
    switchTheme: 'Switch theme',
    themeSystem: 'System theme',
    themeLight: 'Light theme',
    themeDark: 'Dark theme',
  },

  // Navigation
  navigation: {
    dashboard: 'Dashboard',
    market: 'Market',
    backtest: 'Backtest',
    strategyComparison: 'Strategy Comparison',
    strategyRanking: 'Strategy Ranking',
    experimentRanking: 'Experiment Ranking',
    analytics: 'Analytics',
    watchlist: 'Watchlist',
    portfolio: 'Portfolio',
    parameterOptimization: 'Parameter Optimization',
    agent: 'AI Agent',
    trade: 'Alpaca Trade',
    settings: 'Settings',
    tradeMode: 'TRADE MODE',
    paperMode: 'Paper',
    realMode: 'Real',
    researchGroup: 'RESEARCH',
    strategyGroup: 'STRATEGY',
    tradingGroup: 'TRADING',
    systemGroup: 'SYSTEM',
    realTradingBanner: 'REAL TRADING MODE ACTIVE - Live orders will be sent to Alpaca Markets',
    switchToRealTrading: 'Switch to Real Trading Mode',
    switchToRealMode: 'Switch to Real Mode',
    stayInPaperMode: 'Stay in Paper Mode',
    tradeEnvironment: 'Trading Environment',
    currentEnvironment: 'Current',
    paperEnvironment: 'Paper Mode',
    liveEnvironment: 'Destination',
    modeSwitchNoOrder: 'Switching environments does not place an order. It changes the account and destination used by future trade tickets.',
    liveOrdersRequireReview: 'Manual trade tickets require a separate review; authorized automation remains limited by its configured risk controls.',
    liveUsesConnectedAccount: 'Submitted orders use the connected Alpaca live account and may affect real funds.',
    liveRiskAcceptance: 'I understand that future submitted orders can use real funds.',
    realTradingWarning: 'Real Trading Mode can place live orders with real money.',
    realTradingWarningDesc: 'Orders will be sent to api.alpaca.markets. Live funds may be at risk. Only switch if you have configured live credentials and understand the risks.',
    typeRealToConfirm: 'Type "REAL" (all uppercase) to confirm:',
    typeRealPlaceholder: 'Type REAL here',
    typeRealValidation: 'Please type "REAL" (all caps) to confirm.',
    checkConnectionStatus: 'Check connection status in Settings',
    configLabel: 'CONFIG',
    settingsLabel: 'SETTINGS',
    verifyConnections: 'Verify connections',
  },

  // Dashboard page
  dashboard: {
    title: 'Market Dashboard',
    subtitle: 'Real-time overview of quantitative market dynamics and system health.',
    totalBacktests: 'Total Backtests',
    activeStrategies: 'Active Strategies',
    totalProfit: 'Total Profit',
    winRate: 'Win Rate',
    recentBacktests: 'Recent Backtests',
    quickActions: 'Quick Actions',
    runBacktest: 'Run Backtest',
    viewHistory: 'View History',
    compareStrategies: 'Compare Strategies',
    noRecentBacktests: 'No recent backtests',
    systemStatus: 'System Status',
    cpuUsage: 'CPU Usage',
    memoryUsage: 'Memory Usage',
    diskUsage: 'Disk Usage',
    networkStatus: 'Network Status',
    marketOverview: 'Market Overview',
    topGainers: 'Top Gainers',
    topLosers: 'Top Losers',
    mostActive: 'Most Active',
    refreshData: 'Refresh Data',
    loading: 'Loading...',
    errorLoading: 'Error loading data',
    backtestId: 'Backtest ID',
    symbol: 'Symbol',
    strategy: 'Strategy',
    totalReturn: 'Total Return',
    status: 'Status',
    actions: 'Actions',
    viewDetails: 'View Details',
    runQuickBacktest: 'Run Quick Backtest',
    addToWatchlist: 'Add to Watchlist',
    removeFromWatchlist: 'Remove from Watchlist',
    healthy: 'Healthy',
    warning: 'Warning',
    critical: 'Critical',
    online: 'Online',
    offline: 'Offline',
    connected: 'Connected',
    disconnected: 'Disconnected',
    lastUpdated: 'Last Updated',
    totalSymbols: 'Total Symbols',
    gainers: 'Gainers',
    losers: 'Losers',
    avgChange: 'Avg Change',
    mktCap: 'Mkt Cap',
    largestMove: 'Largest Move',
    sectorDistribution: 'Sector Distribution',
    marketBreadth: 'Market Breadth',
    advancingVsDeclining: 'Advancing vs Declining',
    overallStatus: 'Overall Status',
    bullish: 'BULLISH',
    bearish: 'BEARISH',
    neutral: 'NEUTRAL',
    quoteFeed: 'Quote Feed',
    brokerConnection: 'Broker Connection',
    symbols: 'Symbols',
    environment: 'Environment',
    noGainersFound: 'No gainers found',
    noLosersFound: 'No losers found',
    configureMarketData: 'Configure market data API to populate this panel.',
    sectorDataUnavailable: 'Sector data unavailable',
    watchlistSnapshot: 'Watchlist Snapshot',
    manageAll: 'Manage All',
    watchlistEmpty: 'Your watchlist is empty.',
    exploreMarket: 'Explore Market',
    waitingForData: 'Waiting for market data synchronization...',
    authRequired: 'Authentication Required',
    authRequiredDesc: 'Your session has expired. Please sign in again to view market data.',
    apiConfigRequired: 'API Configuration Required',
    apiConfigRequiredDesc: 'Market data API keys are missing. Please configure Alpaca or Finnhub keys in Settings.',
    configure: 'Configure',
    backendConnectionError: 'Backend Connection Error',
    backendConnectionErrorDesc: 'Cannot reach the backend server. Please ensure it is running.',
    apiError: 'API Error',
    apiNoPricesDesc: 'Market data API returned no prices. Verify your API keys in Configuration or check API rate limits.',
    checkConfig: 'Check Config',
    up: 'UP',
    down: 'DOWN',
    configRequired: 'CONFIG REQUIRED',
    signIn: 'SIGN IN',
    noMarketData: 'No market data available',
    marketData: 'Market Data',
  },

  // Market page
  market: {
    title: 'Market Overview',
    subtitle: 'Real-time equity universe and intelligent action center',
    symbol: 'Symbol',
    name: 'Name',
    price: 'Price',
    change: 'Change',
    changePercent: 'Change %',
    volume: 'Volume',
    marketCap: 'Market Cap',
    sector: 'Sector',
    watchlist: 'Add to Watchlist',
    removeWatchlist: 'Remove from Watchlist',
    searchPlaceholder: 'Search by symbol or company name...',
    noResults: 'No instruments found matching your filters',
    analyze: 'Analyze',
    backtest: 'Backtest',
    search: 'Search',
    allSectors: 'All Sectors',
    refreshData: 'Refresh Data',
    dataSyncError: 'Data Sync Error',
    retry: 'Retry',
    totalStocks: 'Total Stocks',
    marketGainers: 'Market Gainers',
    marketLosers: 'Market Losers',
    avgChange: 'Avg Change',
    dataSource: 'Data Source',
    lastSynchronized: 'Last synchronized',
    showing: 'Showing',
    of: 'of',
    assets: 'assets',
    symbols: 'symbols',
    priceHigh: 'Price High',
    priceLow: 'Price Low',
    actions: 'Actions',
    addToWatchlist: 'Add to Watchlist',
    removeFromWatchlist: 'Remove from Watchlist',
    addToList: 'Add to list',
    removeFromList: 'Remove from list',
    maxSymbolsReached: 'Max {max} symbols reached',
    alreadyInList: '{symbol} is already in your list',
    addedToList: 'Added {symbol} to your list',
    removedFromList: 'Removed {symbol} from your list',
    failedToAdd: 'Failed to add {symbol}',
    failedToRemove: 'Failed to remove {symbol}',
    authRequired: 'Authentication required. Please sign in again.',
    apiKeysNotConfigured: 'API keys not configured. Please configure in Settings.',
    searchFailed: 'Search failed',
    alreadyInListShort: 'already in the list',
    noMatchFound: 'No matching company or symbol found. Please check your input.',
    addedSymbol: 'Added {symbol}',
    removedSymbol: 'Removed {symbol}',
    maxSymbolsWarning: 'Maximum of {max} symbols reached. Please remove some before adding more.',
    loadingMarketData: 'Loading market data...',
    failedToLoadMarketData: 'Failed to load market data',
    invalidDataFormat: 'Server returned invalid data format',
    noValidStockData: 'No valid stock data received',
  },

  // Backtest page
  backtest: {
    title: 'Strategy Backtest',
    subtitle: 'Test trading strategy historical performance',
    runBacktest: 'Run Backtest',
    backtestHistory: 'Backtest History',
    parameters: 'Parameters',
    symbol: 'Symbol',
    strategy: 'Strategy',
    period: 'Period',
    initialCapital: 'Initial Capital',
    startDate: 'Start Date',
    endDate: 'End Date',
    runningBacktest: 'Running backtest...',
    backtestCompleted: 'Backtest completed',
    backtestFailed: 'Backtest failed',
    viewDetails: 'View Details',
    compare: 'Compare',
    deleteBacktest: 'Delete Backtest',
    noBacktestHistory: 'No backtest history',
    selectToCompare: 'Select backtests to compare',
    selectedCount: 'Selected {count}',
    results: 'Results',
    tradingChart: 'Trading Chart',
    backToBacktest: 'Back to Backtest',
    // Form
    configuration: 'Backtest Configuration',
    subtitleForm: 'Test a trading idea against historical prices, costs, and risk metrics.',
    engineReady: 'ENGINE READY',
    systemNotification: 'System Notification',
    stockSymbol: 'Stock Symbol / Portfolio',
    stockSymbolHelp: 'Input ticker(s) or company name',
    stockSymbolPlaceholder: 'e.g. AAPL, MSFT, TSLA',
    stockSymbolRequired: 'Please enter stock symbol',
    strategyModel: 'Strategy Model',
    strategyModelRequired: 'Please select a strategy',
    chooseStrategy: 'Choose execution strategy',
    portfolioActive: 'PORTFOLIO ACTIVE',
    portfolioMode: 'Multi-stock mode: {count} symbols loaded for simulation.',
    executionParams: 'Execution Parameters',
    realMarketData: 'Real Market Data',
    // Strategy params
    shortMaPeriod: 'Short MA Period',
    longMaPeriod: 'Long MA Period',
    rsiPeriod: 'RSI Period',
    oversold: 'Oversold',
    overbought: 'Overbought',
    fastEma: 'Fast EMA',
    slowEma: 'Slow EMA',
    signal: 'Signal',
    periodLabel: 'Period',
    stdDev: 'Std Dev',
    momentumPeriod: 'Momentum Period',
    lookbackPeriod: 'Lookback Period',
    entryZScore: 'Entry Z-Score',
    exitZScore: 'Exit Z-Score',
    stopLossPct: 'Stop Loss %',
    takeProfitPct: 'Take Profit %',
    oversoldLevel: 'Oversold Level',
    trendMaPeriod: 'Trend MA Period',
    trendFilter: 'Trend Filter',
    enable: 'Enable',
    entryPeriod: 'Entry Period',
    exitPeriod: 'Exit Period',
    atrPeriod: 'ATR Period',
    atrStopMultiple: 'ATR Stop',
    emaPeriod: 'EMA Period',
    atrMultiplier: 'ATR Mult.',
    multiplier: 'Multiplier',
    kPeriod: '%K Period',
    dPeriod: '%D Period',
    adxPeriod: 'ADX Period',
    adxThreshold: 'ADX Threshold',
    fastEmaPeriod: 'Fast EMA',
    slowEmaPeriod: 'Slow EMA',
    // Simulation
    simulationWindow: 'Simulation Window',
    initialLiquidity: 'Initial Liquidity ($)',
    executingSimulation: 'Executing Simulation...',
    executeBacktest: 'Execute Backtest',
    savePlan: 'Save Plan',
    hidePlans: 'Hide Plans',
    savedPlans: 'Saved Plans',
    stageForPaper: 'Stage for Paper',
    strategyStaged: 'Strategy staged for Paper Trading',
    stageLimited: 'Stage support currently limited to MA Crossover',
    // Strategy library
    strategyLibrary: 'Strategy Library',
    noSavedPlans: 'No saved strategy plans found.',
    // Recent sessions
    recentSessions: 'Recent Sessions',
    compareCount: 'Compare ({count})',
    noPreviousSessions: 'No previous sessions found.',
    quickInsights: 'Quick Insights',
    exploreTopSymbols: 'Explore Top Symbols',
    loadMsftBlueprint: 'Load MSFT Blueprint',
    loadTslaBlueprint: 'Load TSLA Blueprint',
    msftLoaded: 'Microsoft RSI session loaded',
    tslaLoaded: 'Tesla Momentum session loaded',
    // Report
    backtestReport: 'Backtest Report',
    verified: 'VERIFIED',
    totalReturnLabel: 'Total Return',
    sharpeRatioLabel: 'Sharpe Ratio',
    maxDrawdownLabel: 'Max Drawdown',
    winRateLabel: 'Win Rate',
    totalTrades: 'Total Trades',
    netProfit: 'Net Profit',
    // Tabs
    overview: 'Overview',
    analyticsCharts: 'Analytics Charts',
    tradeLog: 'Trade Log',
    strategyBlueprint: 'Strategy Details',
    // Overview
    strategyModule: 'Strategy Module',
    instrument: 'Instrument',
    initialEquity: 'Initial Equity',
    simulationPeriod: 'Simulation Period',
    performanceBreakdown: 'Performance Breakdown',
    // Result metrics
    strategyLabel: 'Strategy',
    dataMode: 'Data Mode',
    dataSource: 'Data Source',
    statusLabel: 'Status',
    annualizedReturn: 'Annualized Return',
    profitLoss: 'Profit / Loss',
    calmarRatio: 'Calmar Ratio',
    avgPnlPerTrade: 'Avg P&L per Trade',
    sortinoRatio: 'Sortino Ratio',
    avgEquityRatio: 'Avg Equity Ratio',
    volatility: 'Volatility',
    profitFactor: 'Profit Factor',
    expectancy: 'Expectancy',
    // Result descriptions
    descStrategy: 'Strategy used for backtest',
    descDataMode: 'Data mode used for backtest',
    descDataSource: 'Source of data used for backtest',
    descStatus: 'Current backtest status',
    descTotalReturn: 'Total return over the period',
    descAnnualizedReturn: 'Annualized return (CAGR)',
    descProfitLoss: 'Profit/Loss amount',
    descSharpeRatio: 'Annualized Sharpe ratio',
    descCalmarRatio: 'Return vs max drawdown',
    descMaxDrawdown: 'Maximum loss from a peak',
    descWinRate: 'Percentage of winning trades',
    descTrades: 'Total number of trades executed',
    descAvgPnl: 'Average profit/loss per trade',
    descVolatility: 'Daily volatility',
    descSortinoRatio: 'Annualized Sortino ratio',
    descProfitFactor: 'Gross profit divided by gross loss',
    descExpectancy: 'Expected return per trade',
    descExposure: 'Average equity as percentage of initial capital',
    // Charts
    equityGrowthCurve: 'Equity Growth Curve',
    drawdownAnalysis: 'Drawdown Analysis',
    detailedTradingSignals: 'Detailed Trading Signals',
    portfolioNotAvailable: 'Detailed Price Charts not available for Portfolio Mode.',
    portfolioValue: 'Portfolio Value',
    drawdownLabel: 'Drawdown',
    dateLabel: 'Date',
    // Trade log
    winningTrades: 'WINNING TRADES',
    profitFactorLabel: 'PROFIT FACTOR',
    avgProfit: 'AVG PROFIT',
    avgLoss: 'AVG LOSS',
    entryDate: 'Entry Date',
    exitDate: 'Exit Date',
    action: 'Action',
    entryPrice: 'Entry Price',
    exitPrice: 'Exit Price',
    pnlLabel: 'PnL',
    returnPercent: 'Return %',
    holdingDays: 'Holding Days',
    position: 'Position',
    longPosition: 'Long',
    shortPosition: 'Short',
    noTradeData: 'No trade data available',
    totalPnl: 'Total PnL',
    avgReturn: 'Avg Return',
    winningTradesCount: 'Winning Trades',
    losingTradesCount: 'Losing Trades',
    tradeRange: '{start}-{end} of {total} trades',
    pnlDollar: 'P&L ($)',
    returnLabel: 'Return',
    // Blueprint
    coreStrategyInfo: 'Core Strategy Information',
    modelName: 'Model Name',
    dataProvider: 'Data Provider',
    engineVersion: 'ENGINE VERSION',
    appliedParameters: 'Applied Parameters',
    // Strategy names
    strategyMovingAverage: 'Moving Average Crossover',
    strategyRsi: 'RSI Strategy',
    strategyMacd: 'MACD Strategy',
    strategyBollinger: 'Bollinger Bands',
    strategyMomentum: 'Momentum Strategy',
    strategyMeanReversion: 'Mean Reversion',
    strategyBuyHold: 'Buy & Hold Benchmark',
    strategyDonchianBreakout: 'Donchian / Turtle Breakout',
    strategyKeltnerBreakout: 'Keltner Channel Breakout',
    strategySupertrend: 'SuperTrend',
    strategyStochastic: 'Stochastic Reversal',
    strategyAdxTrend: 'ADX Trend Filter',
    // History table
    select: 'Select',
    symbolCol: 'Symbol',
    strategyCol: 'Strategy',
    periodCol: 'Period',
    returnCol: 'Return',
    sharpeCol: 'Sharpe',
    statusCol: 'Status',
    actionsCol: 'Actions',
    delete: 'Delete',
    backtestDeleted: 'Backtest deleted successfully',
    backtestDeleteFailed: 'Failed to delete backtest',
    loadedBacktest: 'Loaded backtest: {symbol} - {strategy}',
    backtestDataNotFound: 'Backtest data not found in local storage',
    failedToLoadResults: 'Failed to load backtest results',
    // Status
    completed: 'Completed',
    failed: 'Failed',
    running: 'Running',
    unknown: 'Unknown',
    realData: 'Real Data',
    financialApis: 'Financial APIs',
    // Chart labels (TradingChart component)
    priceChartWithSignals: 'Price Chart with Trading Signals',
    price: 'PRICE',
    signals: 'SIGNALS',
    volume: 'VOLUME',
    volumeChart: 'VOLUME CHART',
    tradingVolumeGreenRed: 'Trading Volume • Green=Up, Red=Down',
    volumeData: 'VOLUME DATA',
    buySignal: 'Buy Signal',
    sellSignal: 'Sell Signal',
    priceUp: 'PRICE UP',
    priceDown: 'PRICE DOWN',
    // Form defaults
    defaultLabel: 'Default: {value}',
    // Strategy library
    load: 'Load',
    symbolLabel: 'Symbol',
    strategyLabelShort: 'Strategy',
    createdLabel: 'Created',
    // Strategy name mapping (for blueprint display)
    strategyNameMovingAverage: 'Moving Average',
    strategyNameRsi: 'RSI',
    strategyNameMacd: 'MACD',
    strategyNameBollinger: 'Bollinger Bands',
    strategyNameMomentum: 'Momentum',
    strategyNameMeanReversion: 'Mean Reversion',
    strategyNameBuyHold: 'Buy & Hold',
    strategyNameDonchianBreakout: 'Donchian Breakout',
    strategyNameKeltnerBreakout: 'Keltner Breakout',
    strategyNameSupertrend: 'SuperTrend',
    strategyNameStochastic: 'Stochastic',
    strategyNameAdxTrend: 'ADX Trend',
    // Parameter key mapping (for blueprint display)
    paramShortMaPeriod: 'Short MA Period',
    paramLongMaPeriod: 'Long MA Period',
    paramRsiPeriod: 'RSI Period',
    paramRsiOversold: 'RSI Oversold',
    paramRsiOverbought: 'RSI Overbought',
    paramMacdFast: 'MACD Fast',
    paramMacdSlow: 'MACD Slow',
    paramMacdSignal: 'MACD Signal',
    paramBollingerPeriod: 'Bollinger Period',
    paramBollingerStdDev: 'Bollinger StdDev',
    paramMomentumPeriod: 'Momentum Period',
    paramLookbackPeriod: 'Lookback Period',
    paramEntryZScore: 'Entry Z-Score',
    paramExitZScore: 'Exit Z-Score',
    paramStopLossPct: 'Stop Loss %',
    paramTakeProfitPct: 'Take Profit %',
    paramOversoldLevel: 'Oversold Level',
    paramTrendMaPeriod: 'Trend MA Period',
    paramTrendFilter: 'Trend Filter',
    paramEntryPeriod: 'Entry Channel',
    paramExitPeriod: 'Exit Channel',
    paramAtrPeriod: 'ATR Period',
    paramAtrStopMultiple: 'ATR Stop Multiple',
    paramEmaPeriod: 'EMA Period',
    paramAtrMultiplier: 'ATR Multiplier',
    paramMultiplier: 'Multiplier',
    paramKPeriod: '%K Period',
    paramDPeriod: '%D Period',
    paramOversold: 'Oversold',
    paramOverbought: 'Overbought',
    paramAdxPeriod: 'ADX Period',
    paramAdxThreshold: 'ADX Threshold',
    paramFastEmaPeriod: 'Fast EMA Period',
    paramSlowEmaPeriod: 'Slow EMA Period',
    buyHoldDescription: 'Benchmark only: buys on the first available bar and holds to the end of the simulation window.',
    // Data source display
    dataBars1Day: '1 Day bars',
  },

  // Strategy Comparison page
  comparison: {
    title: 'Strategy Comparison',
    subtitle: 'Multi-strategy performance comparison analysis',
    parameterComparison: 'Parameter Comparison',
    performanceComparison: 'Performance Metrics Comparison',
    equityCurveComparison: 'Equity Curve Comparison',
    noBacktestsSelected: 'No backtests selected for comparison',
    goToBacktestPage: 'Go to Backtest Page',
    selectBacktestsPrompt: 'Please go back to the Backtest page and select one or more results to compare.',
    loadingBacktestResults: 'Loading backtest results...',
    failedToLoad: 'Failed to load',
    failedToLoadHistory: 'Failed to load backtest history',
    failedToLoadResults: 'Failed to load backtest results',
    backtestId: 'Backtest ID',
    createdAt: 'Created At',
    status: 'Status',
    completed: 'Completed',
    running: 'Running',
    failed: 'Failed',
    parameter: 'Parameter',
    metric: 'Metric',
    noEquityCurveData: 'No equity curve data available',
    // Selector page
    selectSessionsTitle: 'Select Backtests to Compare',
    selectSessionsSubtitle: 'Choose at least two completed sessions from your history to generate an analytical comparison.',
    backToBacktest: 'Back to Backtest',
    backToComparison: 'Back to Comparison',
    refreshHistory: 'Refresh History',
    sessionsSelected: '{count} SESSIONS SELECTED',
    selectAtLeast2: 'Select at least 2 sessions to compare',
    generateComparison: 'Generate Comparison ({count})',
    noHistoryAvailable: 'No backtest history available yet.',
    noHistoryDesc: 'Run a backtest first, then return here to compare sessions.',
    goToBacktest: 'Go to Backtest',
    // Error/loading
    dataAnalysisError: 'Data Analysis Error',
    returnToSelection: 'Return to Selection',
    backToSessions: 'Back to Sessions',
    initializingEngine: 'Initializing analysis engine...',
    // Status tags
    statusCompleted: 'completed',
    statusRunning: 'running',
    statusFailed: 'failed',
    // Winner tag
    bestPosition: 'Best #{position}',
    // Selector table columns
    colSymbol: 'Symbol',
    colStrategy: 'Strategy',
    colReturn: 'Return',
    colSharpe: 'Sharpe',
    colMaxDD: 'Max DD',
    colWinRate: 'Win Rate',
    colDate: 'Date',
    // Parameter table labels
    paramSymbol: 'Symbol',
    paramStrategy: 'Strategy',
    paramPeriod: 'Period',
    paramInitialCapital: 'Initial Capital',
    paramDataSource: 'Data Source',
    paramDataMode: 'Data Mode',
    paramCreatedAt: 'Created At',
    paramBacktestId: 'Backtest ID',
    paramStatus: 'Status',
    paramStrategyParameters: 'Strategy Parameters',
    // Metric table labels
    metricProfitLoss: 'Profit / Loss',
    metricTotalReturn: 'Total Return',
    metricSharpeRatio: 'Sharpe Ratio',
    metricMaxDrawdown: 'Max Drawdown',
    metricWinRate: 'Win Rate',
    metricTrades: 'Trades',
    metricAnnualizedReturn: 'Annualized Return',
    metricSortinoRatio: 'Sortino Ratio',
    metricVolatility: 'Volatility',
    metricProfitFactor: 'Profit Factor',
    metricExposure: 'Exposure',
    metricExpectancy: 'Expectancy',
    metricCalmarRatio: 'Calmar Ratio',
    metricAvgReturnPerTrade: 'Avg Return per Trade',
    metricBuyHoldReturn: 'Buy & Hold Return',
    // Dashboard
    dashboardTitle: 'Strategy Comparison Dashboard',
    dashboardSubtitle: 'Compare selected backtest sessions across return, risk, and consistency.',
    changeSelection: 'Change Selection',
    // Summary cards
    bestReturn: 'Best Return',
    bestSharpe: 'Best Sharpe',
    lowestDrawdown: 'Lowest Drawdown',
    highestWinRate: 'Highest Win Rate',
    bestProfitFactor: 'Best Profit Factor',
    sessionsCount: 'Sessions',
    // Session cards
    sessionPrefix: 'SESSION {index}',
    returnLabel: 'Return',
    sharpeLabel: 'Sharpe',
    maxDDLabel: 'Max DD',
    winRateLabel: 'Win Rate',
    // Chart/section titles
    sessionRankingTitle: 'Session Ranking & Overview',
    performanceMetricsViz: 'Performance Metrics Visualization',
    riskRewardQuadrant: 'Risk / Reward Quadrant',
    relativeEquityGrowth: 'Relative Equity Growth Comparison',
    detailedMetricMatrix: 'Detailed Metric Matrix & Performance Winners',
    // Column headers
    performanceMetric: 'Performance Metric',
    winner: 'Winner',
    noData: 'No data',
    noEquityCurveDetailed: 'No equity curve data available for detailed comparison',
    backtestPrefix: 'Backtest {index}',
    // Strategy names (selector table)
    strategyNameMaCross: 'MA Crossover',
    strategyNameRsiShort: 'RSI',
    strategyNameMacdShort: 'MACD',
    strategyNameBollingerBands: 'Bollinger Bands',
    strategyNameMomentumShort: 'Momentum',
    // Strategy names (ranking table, short)
    strategyShortMaCross: 'MA Cross',
    strategyShortBb: 'BB',
    strategyShortMom: 'MOM',
    // Chart labels
    chartTotalReturn: 'Total Return',
    chartSharpeRatio: 'Sharpe Ratio',
    chartMaxDrawdown: 'Max Drawdown',
    chartWinRate: 'Win Rate',
    riskLabel: 'Risk (Max DD %)',
    rewardLabel: 'Reward (Return %)',
    strategiesLabel: 'Strategies',
    buyHoldBenchmark: 'Buy & Hold Benchmark',
    // No backtests message
    pleaseSelectAtLeast2: 'Please select at least 2 sessions to compare.',
    backtestN: 'Backtest {index}',
    sessionN: 'Session {index}',
  },

  // Strategy Ranking page
  ranking: {
    title: 'Automated Quant Platform - Strategy Ranking',
    subtitle: 'Strategy leaderboard based on historical performance',
    rank: 'Rank',
    backtestId: 'Backtest ID',
    symbol: 'Symbol',
    strategy: 'Strategy',
    totalReturn: 'Total Return',
    annualizedReturn: 'Annualized Return',
    sharpeRatio: 'Sharpe Ratio',
    maxDrawdown: 'Max Drawdown',
    winRate: 'Win Rate',
    trades: 'Trades',
    avgReturnPerTrade: 'Avg Return/Trade',
    volatility: 'Volatility',
    sortinoRatio: 'Sortino Ratio',
    profitFactor: 'Profit Factor',
    expectancy: 'Expectancy',
    exposure: 'Exposure',
    noRankingData: 'No ranking data available',
    loadingRanking: 'Loading ranking data...',
    refreshRanking: 'Refresh Ranking',
    // Quality labels
    qualityLowSample: 'Low Sample',
    qualityWeak: 'Weak',
    qualityHighRisk: 'High Risk',
    qualityElite: 'Elite',
    qualityStrong: 'Strong',
    qualityStable: 'Stable',
    qualityHighReturn: 'High Return',
    qualityNeutral: 'Neutral',
    // Table columns
    colRank: 'Rank',
    colSymbolStrategy: 'Symbol & Strategy',
    colTotalReturn: 'Total Return',
    colSharpe: 'Sharpe',
    colMaxDD: 'Max DD',
    colWinRate: 'Win Rate',
    colProfFactor: 'Prof. Factor',
    colTrades: 'Trades',
    colDate: 'Date',
    colStatus: 'Status',
    // Loading
    loadingTip: 'Calculating Strategy Rankings...',
    // Leaderboard
    leaderboardTitle: 'Strategy Leaderboard',
    leaderboardSubtitle: 'Performance ranking of all historical backtests. Find the most stable and profitable strategies.',
    refreshData: 'Refresh Data',
    // Summary cards
    rankedSessions: 'Ranked Sessions',
    bestReturnStat: 'Best Return',
    topSharpe: 'Top Sharpe',
    bestMaxDD: 'Best MaxDD',
    topWinRate: 'Top Win Rate',
    strategiesCount: 'Strategies',
    // Filters
    searchPlaceholder: 'Search symbol or strategy...',
    allStrategies: 'All Strategies',
    sortByTotalReturn: 'Total Return',
    sortBySharpeRatio: 'Sharpe Ratio',
    sortByMaxDrawdown: 'Max Drawdown',
    sortByWinRate: 'Win Rate',
    sortByProfitFactor: 'Profit Factor',
    sortByTrades: 'Number of Trades',
    completedOnly: 'Completed Only',
    allStatus: 'All Status',
    // Pagination
    totalStrategies: 'Total {count} strategies',
    // Empty state
    noRankedStrategies: 'No ranked strategies yet',
    runBacktestsFirst: 'Run backtests first to populate strategy rankings.',
    goToBacktest: 'Go to Backtest',
  },

  // Watchlist page
  watchlist: {
    title: 'Watchlist',
    subtitle: 'Monitor and analyze your selected stocks in real-time',
    addSymbol: 'Add Symbol',
    symbolPlaceholder: 'e.g. AAPL, TSLA, NVDA',
    add: 'Add',
    remove: 'Remove',
    noSymbols: 'Your watchlist is empty',
    addSymbolPrompt: 'Add a stock symbol above to start monitoring real-time performance and AI signals.',
    addSymbolToWatchlist: 'Add Symbol to Watchlist',
    symbolAlreadyExists: 'Symbol already exists',
    symbolAdded: 'Symbol added',
    symbolRemoved: 'Symbol removed',
    loadingSymbols: 'Loading watchlist...',
    failedToLoad: 'Failed to load watchlist',
    failedToSave: 'Failed to save watchlist',
    lastPrice: 'Last Price',
    dailyChange: 'Daily Change',
    marketStatus: 'Market Status',
    open: 'Open',
    high: 'High',
    low: 'Low',
    prevClose: 'Prev Close',
    total: 'Total',
    symbols: 'symbols',
    updated: 'Updated',
    avgChange: 'Avg Change',
    watchlistSize: 'WATCHLIST SIZE',
    gainers: 'GAINERS',
    losers: 'LOSERS',
    avgPerformance: 'AVG PERFORMANCE',
    watchlistItems: 'Watchlist Items',
    searchWatchlist: 'Search watchlist...',
    dayRange: 'Day Range',
    signal: 'Signal',
    actions: 'Actions',
    analyze: 'Analyze',
    backtest: 'Backtest',
    pressEnter: 'Press Enter to add',
    inputHint: 'Input company name or ticker',
    dataUnavailable: 'No data',
    tryAAPL: 'Try adding AAPL',
    analyzeTip: 'Analyze for technical context',
    backtestTip: 'Backtest strategy fit',
    sortTip: 'Click headers to sort',
    autoSaveTip: 'Data auto-saves in browser',
    noMatchFound: 'No matching stock found for "{input}"',
    alreadyInWatchlist: '{symbol} is already in your watchlist',
    addedToWatchlist: 'Added {symbol} to watchlist',
    removedFromWatchlist: '{symbol} removed from watchlist',
    failedToAdd: 'Failed to add "{input}". Please try again.',
    pleaseEnterSymbol: 'Please enter a stock symbol or company name',
    clickColumnSort: 'Click column headers to sort',
  },

  // Parameter Optimization page
  optimization: {
    title: 'Parameter Optimization',
    subtitle: 'Search parameter ranges and identify robust strategy configurations for maximum performance.',
    runOptimization: 'Run Optimization',
    optimizationResults: 'Optimization Results',
    statistics: 'Statistics',
    totalCombinations: 'Total Combinations',
    validCombinations: 'Valid Combinations',
    bestReturn: 'Best Return',
    worstReturn: 'Worst Return',
    averageReturn: 'Average Return',
    fastMAValues: 'Fast MA Values',
    fastMATooltip: 'Fast moving average periods to test (e.g., 5, 10, 20)',
    slowMAValues: 'Slow MA Values',
    slowMATooltip: 'Slow moving average periods to test (e.g., 50, 100, 200)',
    period: 'Period',
    initialCapital: 'Initial Capital',
    optimizationRunning: 'Optimization running...',
    optimizationCompleted: 'Optimization completed',
    optimizationFailed: 'Optimization failed',
    noResults: 'No optimization results',
    rank: 'Rank',
    fastMA: 'Fast MA',
    slowMA: 'Slow MA',
    totalReturn: 'Total Return',
    annualized: 'Annualized',
    sharpe: 'Sharpe',
    maxDD: 'Max DD',
    trades: 'Trades',
    loadingOptimization: 'Loading optimization results...',
    failedToRun: 'Failed to run optimization',
    symbol: 'Symbol',
    strategy: 'Strategy',
    topCombination: 'Top Combination',
    // UI labels
    optimizationReady: 'OPTIMIZATION READY',
    engineConfiguration: 'Engine Configuration',
    stockSymbol: 'Stock Symbol',
    strategyModel: 'Strategy Model',
    lookbackPeriod: 'Lookback Period',
    threeMonthsData: '3 Months Data',
    sixMonthsData: '6 Months Data',
    oneYearData: '1 Year Data',
    initialLiquidity: 'Initial Liquidity ($)',
    parameterSearchSpace: 'Parameter Search Space',
    runningGeneticSearch: 'RUNNING GENETIC SEARCH...',
    optimizationSuccess: 'Optimization Success',
    configurationError: 'Configuration Error',
    performanceHeatmap: 'Performance Heatmap',
    detailedResultMatrix: 'Detailed Result Matrix',
    start: 'Start',
    end: 'End',
    step: 'Step',
    // Parameter block titles
    shortMaParameters: 'Short MA Parameters',
    longMaParameters: 'Long MA Parameters',
    momentumPeriodBlock: 'Momentum Period',
    rsiPeriodBlock: 'RSI Period',
    oversoldLevelBlock: 'Oversold Level',
    overboughtLevelBlock: 'Overbought Level',
    fastEmaBlock: 'Fast EMA',
    slowEmaBlock: 'Slow EMA',
    signalEmaBlock: 'Signal EMA',
    periodBlock: 'Period Parameters',
    stdDevBlock: 'Std Dev Parameters',
    lookbackBlock: 'Lookback Period',
    entryZScoreBlock: 'Entry Z-Score',
    exitZScoreBlock: 'Exit Z-Score',
    strategyParamFallback: 'Strategy parameters for {strategy} configuration will be displayed here.',
    // Validation
    validationStartEnd: '{field}: Start ({start}) must be less than or equal to End ({end})',
    validationStep: '{field}: Step ({step}) must be greater than 0',
    validationShortMaLongMa: 'Short MA range should be less than Long MA range for meaningful crossover',
    validationOversoldOverbought: 'Oversold levels must be less than Overbought levels',
    validationFastSlow: 'Fast EMA must be less than Slow EMA',
    validationStdDev: 'Std Dev must be greater than 0',
    // Messages
    optimizationCompletedMsg: 'Optimization completed! Found {count} valid combinations.',
    // Strategy names for dropdown
    strategyMovingAverageCrossover: 'Moving Average Crossover',
    strategyRsiStrategy: 'RSI Strategy',
    strategyMacdStrategy: 'MACD Strategy',
    strategyBollingerBands: 'Bollinger Bands',
    strategyMomentumStrategy: 'Momentum Strategy',
    strategyMeanReversionLabel: 'Mean Reversion',
    // OptimizationSummary
    summaryTitle: 'Optimization Summary',
    combinations: 'Combinations',
    validResults: 'Valid Results',
    bestReturnCard: 'Best Return',
    worstReturnCard: 'Worst Return',
    avgReturn: 'Avg Return',
    bestSharpeCard: 'Best Sharpe',
    bestCombinationTitle: 'Best Combination Identified',
    optimizedBySharpe: 'Optimized by Sharpe Ratio',
    recommended: 'RECOMMENDED',
    paramConfiguration: 'Parameters Configuration',
    perfMetrics: 'Performance Metrics',
    labelSharpeRatio: 'Sharpe Ratio',
    labelTotalReturn: 'Total Return',
    labelMaxDrawdown: 'Max Drawdown',
    labelWinRate: 'Win Rate',
    // Parameter labels
    labelShortMa: 'Short MA',
    labelLongMa: 'Long MA',
    labelRsiPeriod: 'RSI Period',
    labelOversold: 'Oversold',
    labelOverbought: 'Overbought',
    labelFastMa: 'Fast MA',
    labelSlowMa: 'Slow MA',
    labelSignalMa: 'Signal MA',
    labelPeriod: 'Period',
    labelStdDev: 'Std Dev',
    labelMomentumPeriod: 'Momentum Period',
    // OptimizationHeatmap
    heatmapTitle: 'Sharpe Sensitivity',
    heatmapLowPerf: 'Low Performance',
    heatmapNeutral: 'Neutral',
    heatmapOptimal: 'Optimal',
    heatmapUnavailable: 'Visualization Unavailable',
    heatmapUnavailableDesc: 'Heatmap visualization currently supports 2D parameter spaces (e.g., MA Crossover, Bollinger Bands). Multi-dimensional spaces can be analyzed via the Result Matrix.',
    configDetails: 'Configuration Details',
    // OptimizationResultsTable
    colRank: 'Rank',
    colTotalReturn: 'Total Return',
    colSharpe: 'Sharpe',
    colMaxDD: 'Max DD',
    colWinRate: 'Win Rate',
    colTrades: 'Trades',
    resultsRange: '{from}-{to} of {total} results',
  },

  // Analytics page
  analytics: {
    title: 'AlphaLab — Analytics',
    subtitle: 'Strategy analytics and performance insights',
  },

  // Strategy types
  strategies: {
    buy_hold: 'Buy & Hold',
    moving_average: 'Moving Average',
    rsi: 'RSI',
    macd: 'MACD',
    momentum: 'Momentum',
    bollinger: 'Bollinger Bands',
    mean_reversion: 'Mean Reversion',
    donchian_breakout: 'Donchian Breakout',
    keltner_breakout: 'Keltner Breakout',
    supertrend: 'SuperTrend',
    stochastic: 'Stochastic Reversal',
    adx_trend: 'ADX Trend Filter',
  },

  // Backtest periods
  periods: {
    '3m': '3 Months',
    '6m': '6 Months',
    '1y': '1 Year',
  },

  // Metrics
  metrics: {
    totalReturn: 'Total Return',
    annualizedReturn: 'Annualized Return',
    profitLoss: 'Profit/Loss',
    sharpeRatio: 'Sharpe Ratio',
    calmarRatio: 'Calmar Ratio',
    maxDrawdown: 'Max Drawdown',
    winRate: 'Win Rate',
    trades: 'Trades',
    avgReturnPerTrade: 'Avg Return/Trade',
    volatility: 'Volatility',
    sortinoRatio: 'Sortino Ratio',
    profitFactor: 'Profit Factor',
    expectancy: 'Expectancy',
    exposure: 'Exposure',
  },

  // Error messages
  errors: {
    networkError: 'Network error, please check your connection',
    serverError: 'Server error, please try again later',
    invalidSymbol: 'Invalid symbol',
    invalidParameter: 'Invalid parameter',
    dataFetchError: 'Failed to fetch data',
    optimizationFailed: 'Parameter optimization failed',
    backtestFailed: 'Backtest failed',
    unknownError: 'Unknown error',
  },

  // Success messages
  successes: {
    backtestStarted: 'Backtest started',
    backtestCompleted: 'Backtest completed',
    optimizationStarted: 'Parameter optimization started',
    optimizationCompleted: 'Parameter optimization completed',
    symbolAdded: 'Symbol added to watchlist',
    symbolRemoved: 'Symbol removed from watchlist',
    settingsSaved: 'Settings saved',
  },

  // Warning messages
  warnings: {
    selectBacktestToCompare: 'Please select at least one backtest to compare',
    noBacktestSelected: 'No backtest selected',
    symbolAlreadyInWatchlist: 'Symbol already in watchlist',
    insufficientData: 'Insufficient data for calculation',
    highRisk: 'High risk warning',
  },

  // Language switching
  language: {
    english: 'English',
    chinese: '中文',
    switchLanguage: 'Switch Language',
    currentLanguage: 'Current Language',
  },

  // Landing Page
  landing: {
    // Navigation
    navHome: 'Home',
    navPlatform: 'Platform',
    navWorkflow: 'Workflow',
    navFeatures: 'Features',
    navTechnology: 'Technology',
    navSecurity: 'Security',
    signIn: 'Sign In',
    getStarted: 'Get Started',
    ariaLabelTechPage: 'Technology page',
    ariaLabelSwitchLang: 'Switch Language',
    ariaLabelGetStarted: 'Get started with AlphaLab',
    ariaLabelSignIn: 'Sign in to AlphaLab',

    // Hero
    heroBadge: 'Research-First Quant Automation',
    heroTitle1: 'AI-Powered Quant Research.',
    heroTitle2: 'Systematic Validation.',
    heroSubtitle: 'AlphaLab scans your configured market universe, validates signals with multi-model AI, runs backtests, and generates risk-aware entry plans — a research-first workflow where you stay in control of every decision.',
    startBuilding: 'Start Building',
    explorePlatform: 'Explore Platform',

    // Market Field landing experience
    marketField: {
      navResearch: 'Research',
      eyebrow: 'QUANT RESEARCH, MADE VISIBLE',
      titleLine1: 'TRACE THE SIGNAL',
      titleLine2: 'KEEP THE DECISION',
      subtitle: 'From 8,421 symbols to one risk-aware plan. Every step stays visible.',
      exploreWorkflow: 'Explore the workflow',
      openPlatform: 'Open the platform',
      observed: 'OBSERVED',
      shortlisted: 'SHORTLISTED',
      validated: 'VALIDATED',
      riskPlan: 'RISK PLAN',
      sampleMode: 'SAMPLE / PAPER MODE',
      momentum: 'Momentum',
      walkForwardAnalysis: 'Walk-forward analysis',
      equityWalkForward: 'Equity (walk-forward)',
      benchmark: 'Benchmark (SPY)',
      walkForward: 'Walk-forward',
      sharpe: 'Sharpe',
      maxDrawdown: 'Max drawdown',
      hardGatesPassed: 'Hard gates passed',
      riskBudget: 'Risk budget',
      manualApproval: 'Manual approval',
      tradePlan: 'TRADE PLAN (NEXT SIGNAL)',
      entry: 'ENTRY',
      stop: 'STOP',
      target: 'TARGET',
      evidence: 'EVIDENCE',
      evidenceMomentum: 'Momentum regime',
      evidenceEarnings: 'Earnings quality',
      evidenceFlow: 'Flow / positioning',
      evidenceVolatility: 'Volatility structure',
      evidenceTechnical: 'Technical confirmation',
      scrollCue: 'SCROLL TO FOLLOW ONE SIGNAL',
      storyEyebrow: 'ONE SIGNAL · FOUR RESEARCH STATES',
      storyTitle: 'Watch the evidence gather around a decision.',
      storySubtitle: 'The market stays in view while AlphaLab adds one verifiable layer at a time.',
      stageObserve: 'OBSERVE',
      stageObserveShort: 'Collect thousands of signals',
      stageObserveTitle: 'Read the market as a field, not a feed.',
      stageObserveDesc: 'Price, volatility, liquidity, relative strength, and event risk remain visible together before any candidate is promoted.',
      stageFilter: 'FILTER',
      stageFilterShort: 'Narrow the universe',
      stageFilterTitle: 'Let hard evidence narrow the universe.',
      stageFilterDesc: 'Deterministic gates remove weak liquidity, poor structure, and unreliable evidence before model review begins.',
      stageTest: 'TEST',
      stageTestShort: 'Validate out-of-sample',
      stageTestTitle: 'Test the idea where it can fail.',
      stageTestDesc: 'Walk-forward folds, costs, benchmark-relative return, drawdown, and parameter stability expose fragile strategies.',
      stagePlan: 'PLAN',
      stagePlanShort: 'Build the risk-aware plan',
      stagePlanTitle: 'Turn evidence into a bounded plan.',
      stagePlanDesc: 'Entry, stop, target, position size, and approval state come together without hiding the assumptions behind them.',
      candidates: 'Candidates',
      liquidityGate: 'Liquidity gate',
      structureGate: 'Structure gate',
      evidenceGate: 'Evidence reliability',
      passed: 'PASSED',
      review: 'REVIEW',
      blocked: 'BLOCKED',
      candidateCount: '24 of 8,421',
      validationCount: '3 of 24',
      awaitingApproval: 'Awaiting manual approval',
      paperReady: 'Paper mode ready',
      researchProof: 'RESEARCH THAT SHOWS ITS WORK',
      researchProofTitle: 'Every result keeps its evidence attached.',
      researchProofDesc: 'Scanner factors, walk-forward validation, trading costs, risk gates, and execution controls remain auditable from research to order.',
      ctaTitle: 'Follow your next idea all the way through.',
      ctaDesc: 'Start in paper mode, inspect every gate, and decide when a plan is ready.',
      ctaPrimary: 'Start building',
      ctaSecondary: 'Review the workflow',
    },

    // Mockup Dashboard
    orchestrator: 'AI AGENT ORCHESTRATOR',
    globalMarketScan: 'Market Scan',
    complete: 'Complete',
    processedEquities: 'Processed 8,432 equities across 14 factors.',
    deeperValidation: 'Deeper Validation',
    analyzing: 'Analyzing',
    llmReview: 'LLM reviewing multi-timeframe divergence.',
    backtestEntryPlan: 'BACKTEST & ENTRY PLAN',
    synthesizingStrategy: 'Synthesizing Strategy',
    riskProtocol: 'Risk Protocol',
    strictPerTrade: 'Strict (1% per trade)',
    targetRR: 'Target R:R',

    // Project Overview
    overviewTitle: 'From Market Scan to Risk-Aware Execution',
    overviewDesc: 'AlphaLab integrates automated market scanning, multi-model AI signal validation, historical backtesting, and risk-aware entry planning into one systematic quant workflow. Every trade idea moves through a structured validation pipeline — AI assists, but you make the final call.',

    // Built With
    builtWith: 'Built With',

    // Core Capabilities
    capabilitiesTitle: 'Core Capabilities',
    capabilitiesSubtitle: 'A complete quant workflow — scan, validate, backtest, plan, execute.',
    capMarketScanning: 'Automated Market Scanning',
    capMarketScanningDesc: 'Scan thousands of equities across multiple timeframes and technical factors. Surface candidates, not noise.',
    capAIValidation: 'Multi-Model AI Validation',
    capAIValidationDesc: 'Specialized models audit structure, momentum, and multi-timeframe confluence. Higher confidence through cross-validation.',
    capBacktesting: 'Historical Backtesting',
    capBacktestingDesc: 'Validate strategy hypotheses against years of market data. Understand edge before committing capital.',
    capEntryPlanning: 'Entry Plan Generation',
    capEntryPlanningDesc: 'Auto-generated entry, stop-loss, and target levels with position sizing based on your risk parameters.',
    capRiskControls: 'Risk Engine',
    capRiskControlsDesc: 'Per-trade exposure limits, drawdown guards, and portfolio-level monitoring. Risk stays within boundaries you define.',
    capExecution: 'Paper & Live Execution',
    capExecutionDesc: 'Validate in simulation first. Route to your broker only when ready. Paper trading is the default.',

    // Feature chips (hero)
    chipScanning: 'Automated market scanning',
    chipAI: 'Multi-model AI validation',
    chipRisk: 'Risk-aware position sizing',
    chipBacktest: 'Historical backtesting',

    // Dashboard preview labels
    dashPreviewStatus: 'Research Engine Online',
    dashPreviewRisk: 'Risk Layer Active',
    dashPreviewBuy: 'VALIDATED',
    dashPreviewSignal: 'Signal Validation',
    dashPreviewActive: 'Analyzing',
    dashPreviewAiOrchestrator: 'Research Pipeline',
    dashPreviewGlobalScan: 'Market Scan',
    dashPreviewDone: 'Passed',
    dashPreviewProcessed: 'Coverage: 8,421 equities',
    dashPreviewTopSignals: 'Signal Research Queue',
    dashPreviewMomentum: 'Momentum Breakout',
    dashPreviewWatch: 'WATCH',
    dashPreviewMeanReversion: 'Mean Reversion',
    dashPreviewTrendContinuation: 'Trend Continuation',
    dashPreviewEarningsMomentum: 'Earnings Momentum',
    dashPreviewEquityCurve: 'Equity Curve Preview',
    dashPreviewYTD: 'YTD',
    dashPreview1M: '1M',
    dashPreview1W: '1W',
    dashPreviewWinRate: 'Win Rate',
    dashPreviewMaxDD: 'Max Drawdown',
    dashPreviewRiskExp: 'Risk Exposure',
    dashPreviewRiskLow: 'Low',
    dashPreviewSharpe: 'Sharpe Ratio',
    dashPreviewMarketPulse: 'Research Feed',
    dashPreviewSectorPerf: 'Signal Archive',
    dashPreviewLiveFeed: 'Recent Validations',
    dashPreviewAutoRouting: 'Awaiting Approval',
    dashPreviewConfidence: 'Confidence',
    dashPreviewStatus2: 'Backtest Passed',
    dashPreviewStatus3: 'Risk Check OK',
    dashPreviewStatus4: 'Awaiting Review',

    // Example Output
    exampleOutputTitle: 'Actionable Intelligence',
    exampleOutputSubtitle: 'See how AlphaLab transforms a market scan into a concrete plan. Note: This is an illustrative example, not financial advice.',

    // Trust Bar
    trustEmail: 'Email verification',
    trustTurnstile: 'Turnstile protected',
    trustEncryption: 'Encrypted API keys',
    trustPaperFirst: 'Paper-first workflow',

    // GitHub
    githubTitle: 'Open & Transparent',
    githubDesc: 'Explore the codebase, contribute to the engine, or deploy your own instance of the AlphaLab pipeline.',
    githubButton: 'GitHub Repository',

    // CTA
    ctaTitle: 'Build a research workflow',
    ctaDesc: 'Start in paper mode, inspect each gate, and decide what is ready for execution.',
    ctaGetStarted: 'Get Started',
    ctaSignIn: 'Sign In',

    // Footer
    footerTagline: 'Research-first quant automation for market scanning, signal validation, and risk-aware planning.',
    footerProduct: 'Product',
    footerTrust: 'Policies & safety',
    footerResources: 'Resources',
    footerPrivacyPolicy: 'Privacy Policy',
    footerTermsOfService: 'Terms of Service',
    footerGithub: 'GitHub',
    footerSecureEnv: 'Paper-first research workspace',
    footerDisclaimer: 'Disclaimer: AlphaLab is for research, education, and decision-support purposes only. It does not provide financial advice. Past performance does not guarantee future results. Real trading involves significant risk of capital loss. Always do your own research before making investment decisions.',
    footerPrivacy: 'Privacy & Security Notice: The platform may process account, market, and AI-provider configuration data. Users should not enter or expose sensitive API keys in public repositories. Personal/account information may be required for authentication, provider configuration, or trading integrations. Users are responsible for their own decisions.',
    footerCopyright: '© {year} AlphaLab. Quantitative research workspace.',
  },

  about: {
    backToHome: 'Back to Home',
    getStarted: 'Get Started',
    title: 'What is AlphaLab?',
    intro: 'AlphaLab brings market screening, validation, risk planning, and execution review into one research workspace. Models can review context, while deterministic gates and user approval remain in control.',
    workflowTitle: 'Research and execution workflow',
    workflowScannerTitle: '1. Market Scanner',
    workflowScannerDesc: 'Screen stocks across multiple sectors using technical indicators and strategy matching.',
    workflowContinueTitle: '2. Candidate Review',
    workflowContinueDesc: 'Saved filters and ranking rules narrow the scan to a reviewable candidate set.',
    workflowFineTitle: '3. Fine Scan',
    workflowFineDesc: 'Review liquidity, news context, market structure, and risk evidence for selected symbols.',
    workflowValidationTitle: '4. Deeper Validation',
    workflowValidationDesc: 'Run out-of-sample tests, cost checks, and stability gates before a candidate can advance.',
    workflowEntryTitle: '5. Entry Plan',
    workflowEntryDesc: 'Deterministic entry price, stop-loss, and target calculation with position sizing.',
    workflowWatchlistTitle: '6. Position Monitoring',
    workflowWatchlistDesc: 'Track approved plans, open orders, position risk, and review-required changes.',
    tradingModesTitle: 'Paper Trading vs Real Trading',
    tradingModesDesc: 'AlphaLab supports both paper trading (simulated) and real trading via Alpaca Markets. Paper trading uses real market data but executes orders in a simulated environment. Real trading connects to Alpaca live API for actual order execution.',
    apiTitle: 'API Configuration',
    apiDesc: 'Connect Alpaca, Finnhub, and a supported model provider from Settings. Saved credentials are masked in the interface and used by backend integrations rather than returned to the browser.',
    riskTitle: 'Risk Control Philosophy',
    riskDesc: 'Configured limits are checked as a candidate moves from research to an order review.',
    riskPositionSizing: 'Position sizing based on account equity and risk per trade',
    riskConcentration: 'Maximum position concentration limits',
    riskDailyLoss: 'Daily loss limits',
    riskAiAssessment: 'Context review alongside deterministic risk gates',
    riskBacktest: 'Backtesting validation before live execution',
    disclaimerTitle: 'Disclaimer',
    disclaimerDesc: 'AlphaLab is a research and analysis tool. Nothing on this platform constitutes financial advice. Past performance does not guarantee future results. Trading stocks involves risk of capital loss. Always conduct your own research and consult a qualified financial advisor before making investment decisions. The creators of AlphaLab are not responsible for any losses incurred from using this platform.',
  },

  // Sector / industry name mapping (key = English name from API, value = display text)
  sectors: {
    'Technology': 'Technology',
    'Semiconductors': 'Semiconductors',
    'Banking': 'Banking',
    'Automobiles': 'Automobiles',
    'Automobile': 'Automobile',
    'Auto': 'Auto',
    'Financial Services': 'Financial Services',
    'Financial': 'Financial',
    'Communications': 'Communications',
    'Communication': 'Communication',
    'Communication Services': 'Communication Services',
    'Media': 'Media',
    'Retail': 'Retail',
    'Consumer': 'Consumer',
    'Consumer Cyclical': 'Consumer Cyclical',
    'Consumer Defensive': 'Consumer Defensive',
    'Healthcare': 'Healthcare',
    'Health': 'Health',
    'Medical': 'Medical',
    'Energy': 'Energy',
    'Oil': 'Oil',
    'Gas': 'Gas',
    'Industrials': 'Industrials',
    'Industrial': 'Industrial',
    'Manufacturing': 'Manufacturing',
    'Real Estate': 'Real Estate',
    'Estate': 'Estate',
    'Utilities': 'Utilities',
    'Utility': 'Utility',
    'Materials': 'Materials',
    'Basic Materials': 'Basic Materials',
    'Material': 'Material',
    'Information Technology': 'Information Technology',
    'Information': 'Information',
    'Telecommunications': 'Telecommunications',
    'Insurance': 'Insurance',
    'Pharmaceuticals': 'Pharmaceuticals',
    'Biotechnology': 'Biotechnology',
    'Software': 'Software',
    'Hardware': 'Hardware',
    'Internet': 'Internet',
    'E-Commerce': 'E-Commerce',
    'Entertainment': 'Entertainment',
    'Transportation': 'Transportation',
    'Logistics': 'Logistics',
    'Construction': 'Construction',
    'Agriculture': 'Agriculture',
    'Defense': 'Defense',
    'Aerospace': 'Aerospace',
    'Aerospace & Defense': 'Aerospace & Defense',
    'Beverages': 'Beverages',
    'Food': 'Food',
    'Services': 'Services',
    'Apparel': 'Apparel',
    'Textiles': 'Textiles',
    'Chemicals': 'Chemicals',
    'Metals': 'Metals',
    'Mining': 'Mining',
    'Other': 'Other',
    'General': 'General',
    'Stocks': 'Stocks',
  } as Record<string, string>,

  // Symbol Analysis page
  analysis: {
    priceAnalysis: 'Price Analysis',
    technicalSignalSummary: 'Technical Signal Summary',
    tradeSetupReference: 'Trade Setup Reference',
    technicalIndicators: 'Technical Indicators',
    companyInsights: 'Company Insights',
    recentPerformance: 'Recent Performance',
    trendBias: 'Trend Bias',
    momentumRSI: 'Momentum (RSI)',
    rangePosition: 'Range Position',
    priceVsSMA20: 'Price vs SMA20',
    priceVsSMA50: 'Price vs SMA50',
    sma20VsSMA50: 'SMA20 vs SMA50',
    volumeTrend: 'Volume Trend',
    supportResistance: 'Support & Resistance',
    supportLevels: 'SUPPORT LEVELS',
    resistanceLevels: 'RESISTANCE LEVELS',
    riskManagement: 'Risk Management',
    entryTarget: 'Entry Target',
    stopLoss: 'Stop Loss',
    takeProfit: 'Take Profit',
    riskRewardRatio: 'Risk / Reward Ratio',
    excellentSetup: 'Excellent Setup',
    goodSetup: 'Good Setup',
    fairSetup: 'Fair Setup',
    poorSetup: 'Poor Setup',
    movingAverages: 'Moving Averages',
    relativeStrength: 'Relative Strength (RSI)',
    analysisInsights: 'Analysis Insights',
    monthReturn: '1 Month Return',
    threeMonthReturn: '3 Month Return',
    yearToDate: 'Year to Date',
    strongBullish: 'Strong Bullish',
    bullish: 'Bullish',
    strongBearish: 'Strong Bearish',
    bearish: 'Bearish',
    neutral: 'Neutral',
    overbought: 'Overbought',
    oversold: 'Oversold',
    near52WHigh: 'Near 52W High',
    near52WLow: 'Near 52W Low',
    upperRange: 'Upper range',
    midRange: 'Mid-range',
    lowerRange: 'Lower range',
    dailyChange: 'Daily Change',
    periodChange: 'Period Change',
    lastClose: 'Last Close',
    analyzingData: 'Analyzing Data...',
    noRSIData: 'No RSI Data',
    insufficientRSI: 'Insufficient data to calculate RSI (14)',
    marketDataDelayed: 'Market data delayed by 15 min',
    dayHigh: 'Day High',
    dayLow: 'Day Low',
    prevClose: 'Prev Close',
    marketCap: 'Market Cap',
    volume: 'Volume',
    currentPrice: 'Current Price',
    runBacktest: 'Run Backtest',
    refresh: 'Refresh',
    chartDataUnavailable: 'Chart data unavailable',
    noHistoricalBars: 'No historical bars were returned for',
    retryLoadingData: 'Retry Loading Data',
    yearHigh: '52W High',
    yearLow: '52W Low',
    change: 'Change',
    analyzing: 'Analyzing Data...',
    trendDescription: 'Overall market momentum based on price action and moving average alignment across {count} data points.',
    rsiDescription: 'Relative Strength Index (14) identifies potential reversal points and trend strength. Current RSI: {value}',
    rangeDescription: 'Position relative to 52-week high ({high}) and low ({low}).',
    maInsufficient: 'Insufficient data for complete MA analysis.',
    maAbove20: 'Price is trading above its short-term 20-day average, indicating immediate bullish momentum.',
    maBelow20: 'Price is currently below the 20-day average, suggesting short-term weakness.',
    ma20Above50: 'The 20-day SMA is above the 50-day SMA, confirming a healthy medium-term uptrend.',
    sector: 'SECTOR',
    industry: 'INDUSTRY',
    peRatio: 'P/E RATIO',
    divYield: 'DIV YIELD',
    dataUpdated: 'Data updated:',
    // Timeframe labels
    tf1Day: '1 Day',
    tf1Week: '1 Week',
    tf1Month: '1 Month',
    tf3Months: '3 Months',
    tf1Year: '1 Year',
  } as Record<string, string>,

  // Signal labels for Watchlist
  signals: {
    'Bullish': 'Bullish',
    'Bearish': 'Bearish',
    'Oversold': 'Oversold',
    'Near support': 'Near support',
    'Near resistance': 'Near resistance',
    'No clear setup': 'No clear setup',
    'Mild bullish': 'Mild bullish',
    'Mild bearish': 'Mild bearish',
    'Data unavailable': 'Data unavailable',
  } as Record<string, string>,

  // AI Agent page
  agent: {
    // Page header
    pageTitle: 'AI Agent',
    pageSubtitle: 'Autonomous intelligence for market screening, technical verification, and automated trading.',
    systemOnline: 'SYSTEM ONLINE',
    // AI Config strip
    aiProvider: 'AI Provider',
    model: 'Model',
    notSet: 'Not set',
    aiStatus: 'AI Status',
    connected: 'CONNECTED',
    notTested: 'NOT TESTED',
    error: 'ERROR',
    notConfigured: 'NOT CONFIGURED',
    marketData: 'Market Data',
    trading: 'Trading',
    paper: 'PAPER',
    notLinked: 'NOT LINKED',
    manageSettings: 'Manage Settings',
    // A. Trading Account Mode
    tradingAccountMode: 'Trading Account Mode',
    paperTrading: 'Paper Trading',
    realTrading: 'Real Trading',
    tradingAccountDesc: 'Used for Entry Plan position sizing and risk checks.',
    tradingAccountDescNote: '(Mode switch is for planning only; no real orders are triggered here)',
    tradingModeRealHint: 'Live mode: used for position sizing and risk checks.',
    tradingModePaperHint: 'Paper mode: simulation trading with virtual funds.',
    pipelineModeAiDesc: 'AI Mode: automatically runs scanning, entry planning, and exit scanning. Auto-submits buy/sell orders.',
    pipelineModeHybridDesc: 'Hybrid Mode: AI suggests, you confirm. No auto order submission.',
    pipelineModeManualDesc: 'Manual Mode: you run each step manually. No auto ordering.',
    syncAccountData: 'Synchronizing account data...',
    accountType: 'Account Type',
    live: 'LIVE',
    status: 'Status',
    cash: 'Cash',
    buyingPower: 'Buying Power',
    portfolioValue: 'Portfolio Value',
    equity: 'Equity',
    dayPL: 'Day P/L',
    totalPL: 'Total P/L',
    positions: 'Positions',
    openOrders: 'Open Orders',
    noAccountData: 'No account data available',
    configureAlpaca: 'Configure Alpaca API in Settings to enable trading.',
    // A2. Trading Preferences
    tradingPreferences: 'Trading Preferences',
    riskProfile: 'Risk Profile',
    riskProfileDesc: 'Affects entry decisions, position sizing, and stop/target levels.',
    lowRisk: 'Low Risk',
    mediumRisk: 'Medium Risk',
    highRisk: 'High Risk',
    timeHorizon: 'Time Horizon',
    timeHorizonDesc: 'Affects trend focus, stop width, and exit timing.',
    shortTerm: 'Short-term',
    midTerm: 'Mid-term',
    longTerm: 'Long-term',
    // A3. Leveraged ETF
    leveragedAlternative: 'Leveraged Alternative Suggestion',
    leveragedRiskWarning: 'amplified volatility and loss risk',
    leveragedFractionable: 'Fractionable',
    leveragedWholeShares: 'Whole shares only',
    // B. AI Watchlist
    aiWatchlist: 'AI Watchlist',
    watchlist: 'Watchlist',
    symbol: 'Symbol',
    company: 'Company',
    price: 'Price',
    change: 'Change',
    score: 'Score',
    confidence: 'Confidence',
    risk: 'Risk',
    entry: 'Entry',
    stopLoss: 'Stop Loss',
    takeProfit: 'Take Profit',
    reason: 'Reason',
    actions: 'Actions',
    buy: 'Buy',
    sell: 'Sell',
    modeAI: 'AI',
    modeHybrid: 'Hybrid',
    modeManual: 'Manual',
    scheduleOff: 'Off',
    schedule15m: '15m',
    schedule30m: '30m',
    schedule1h: '1h',
    schedule2h: '2h',
    lastRunLabel: 'Last run: ',
    nextRunLabel: 'Next: ',
    statusOK: 'OK',
    statusFailed: 'FAILED',
    statusStopped: 'STOPPED',
    viewDetails: 'View Details',
    addToWatchlist: 'Add to Watchlist',
    remove: 'Remove',
    noWatchlistItems: 'No watchlist items',
    clearWatchlist: 'Clear AI Watchlist',
    clearWatchlistConfirm: 'Are you sure you want to clear all items?',
    // C. AI Execution
    aiExecution: 'AI Execution',
    executionCandidates: 'Execution Candidates',
    executionLog: 'Execution Log',
    orderPreview: 'Order Preview',
    submitted: 'Submitted',
    failed: 'Failed',
    pending: 'Pending',
    filled: 'Filled',
    rejected: 'Rejected',
    canceled: 'Canceled',
    modeUsed: 'Mode Used',
    endpointUsed: 'Endpoint Used',
    riskGate: 'Risk Gate',
    riskBlocked: 'Risk Blocked',
    confirmationRequired: 'Confirmation Required',
    configRequired: 'Config Required',
    authRequired: 'Auth Required',
    apiError: 'API Error',
    noExecutionCandidates: 'No execution candidates',
    manualModeReviewOnly: 'Manual mode — review only. No orders will be placed.',
    // D. Market Scanner
    marketScanner: 'Market Scanner',
    runScanner: 'Run Scanner',
    stop: 'Stop',
    scanning: 'Scanning',
    completed: 'Completed',
    progress: 'Progress',
    candidates: 'Candidates',
    trend: 'Trend',
    momentum: 'Momentum',
    volume: 'Volume',
    volatility: 'Volatility',
    newsSentiment: 'News Sentiment',
    eventRisk: 'Event Risk',
    sector: 'Sector',
    aiReasoning: 'AI Reasoning',
    dataSource: 'Data Source',
    lastUpdated: 'Last Updated',
    // E. Continue Scan
    preferredContinueScanList: 'Preferred Continue Scan List',
    continueScan: 'Continue Scan',
    continue: 'Continue',
    watch: 'Watch',
    skip: 'Skip',
    selectedBy: 'Selected By',
    continueCandidate: 'Continue Candidate',
    watchToValidate: 'Watch-to-Validate',
    priority: 'Priority',
    recommendation: 'Recommendation',
    noContinueCandidates: 'No continue list candidates available',
    scanInterrupted: 'Scan interrupted by page refresh. Results preserved.',
    // F. Fine Scan
    fineScan: 'Fine Scan',
    entryQuality: 'Entry Quality',
    good: 'Good',
    extended: 'Extended',
    waitForPullback: 'Wait for Pullback',
    chasing: 'Chasing',
    riskLevel: 'Risk Level',
    backtest: 'Backtest',
    optimization: 'Optimization',
    strategy: 'Strategy',
    quickTest: 'Quick Test',
    totalReturn: 'Total Return',
    sharpeRatio: 'Sharpe Ratio',
    maxDrawdown: 'Max Drawdown',
    winRate: 'Win Rate',
    profitFactor: 'Profit Factor',
    tradeCount: 'Trade Count',
    signalSummary: 'Signal Summary',
    limitedSample: 'LIMITED SAMPLE',
    noFineScanCandidates: 'No qualified Fine Scan candidates. Run Fine Scan first or adjust criteria.',
    fineScanComplete: 'Fine Scan complete: {count} candidates analyzed',
    fineScanFailed: 'Fine scan failed',
    // G. Deeper Validation
    deeperValidation: 'Deeper Validation',
    validation: 'Validation',
    oneYearBacktest: '1 Year Backtest',
    lightOptimization: 'Light Optimization',
    parameterStability: 'Parameter Stability',
    stable: 'Stable',
    unstable: 'Unstable',
    recentPerformance: 'Recent Performance',
    longTermPerformance: 'Long-term Performance',
    pass: 'Pass',
    fail: 'Fail',
    validate: 'Validate',
    validationResult: 'Validation Result',
    allValidationFailed: 'All validation symbols failed',
    validationPartial: 'Validation partial: {succeeded} succeeded, {failed} failed',
    validationCompleted: 'Deeper validation completed for {count} results',
    statusValidating: 'VALIDATING',
    statusInterrupted: 'INTERRUPTED',
    runValidation: 'Run Validation',
    dvSourceContinue: 'Continue',
    dvSourceWatch: 'Watch→Val',
    dvTrendImproving: 'Improving',
    dvTrendConsistent: 'Consistent',
    dvTrendWeakening: 'Weakening',
    dvVerdictConfirmed: 'Confirmed',
    dvVerdictRejected: 'Rejected',
    dvVerdictReview: 'Review',
    dvVerdictPass: 'Pass',
    dvVerdictWatch: 'Watch',
    recentVsLongTermLabel: 'Recent vs Long-Term',
    verdictReasons: 'Verdict Reasons',
    limitedSampleWarn: 'Limited sample: only {trades} trade(s), {combos} combo(s) tested. Validation confidence is reduced.',
    noRiskGateData: 'No risk gate data',
    sourceInternalBacktest: 'Source: Internal Backtest',
    sourceInternalOptimization: 'Source: Internal Optimization',
    qualityLabel: 'Quality',
    statusLabel: 'Status',
    dataLabel: 'Data',
    finalValidationComplete: 'Final validation complete. No critical alerts identified.',
    noLosses: 'no losses',
    stableLabel: 'Stable',
    moderateLabel: 'Moderate',
    weakLabel: 'Weak',
    testedCombos: 'Tested Combos',
    validCombos: 'Valid Combos',
    bestSharpe: 'Best Sharpe',
    avgReturn: 'Avg Return',
    medianReturn: 'Median Return',
    positiveRatio: 'Positive Ratio',
    returnSpread: 'Return Spread',
    topResults: 'Top Results',
    profitableRatio: 'Profitable Ratio',
    stableParams: 'Stable Params',
    longReturn: 'Long Return',
    recentReturn: 'Recent Return',
    longSharpe: 'Long Sharpe',
    recentSharpe: 'Recent Sharpe',
    longDD: 'Long DD',
    recentDD: 'Recent DD',
    // H. Entry Plan
    entryPlan: 'Entry Plan',
    entryZone: 'Entry Zone',
    setup: 'Setup',
    trigger: 'Trigger',
    recommendedAction: 'Recommended Action',
    planDetails: 'Plan Details',
    noEntryPlans: 'No entry plans generated',
    positionSize: 'Position Size',
    invalidation: 'Invalidation',
    riskReward: 'Risk/Reward',
    tp1: 'TP1',
    tp2: 'TP2',
    // Exit Scan
    exitScan: 'Position & Exit',
    noHoldingsToScan: 'No current holdings to scan.',
    exitScanCompleted: 'Exit Scan completed: {count} positions scanned',
    exitScanFailed: 'Exit Scan failed',
    // AI Pipeline
    aiPipeline: 'AI Pipeline',
    runPipeline: 'Run Manual Pipeline',
    runAutoPipelineNow: 'Run Auto Pipeline Now',
    // Market Auto Run
    marketAutoRun: 'Market Auto Run',
    marketAutoRunSubtitle: 'Background scheduled pipeline during market hours.',
    backgroundSchedule: 'Background Schedule',
    scheduleHelperText: 'Runs only during US market hours, 09:30–16:00 ET.',
    lastAutoScan: 'Last Auto Scan',
    nextAutoScan: 'Next Auto Scan',
    autoStatus: 'Auto Status',
    pipelineRunning: 'Pipeline Running',
    pipelineComplete: 'Pipeline complete!',
    pipelineDisabled: 'Disabled while AI Pipeline is running.',
    scanAlreadyRunning: 'A scan is already running. Stop it before starting AI Pipeline.',
    // Pipeline stage labels
    pipelineStageMarketScanner: 'Market Scanner',
    pipelineStageContinueScan: 'Continue Scan',
    pipelineStageFineScan: 'Fine Scan',
    pipelineStageDeeperValidation: 'Deeper Validation',
    pipelineStageEntryPlan: 'Entry Plan',
    pipelineStageExitScan: 'Exit Scan',
    // Schedule status
    scheduleRunningWait: 'Pipeline running — next run will wait',
    scheduleEvery: 'Scheduled: Every ',
    disabled: 'Disabled',
    runsToday: 'Runs Today',
    savedConfig: 'Saved Config',
    customConfig: 'custom',
    defaultConfig: 'default',
    savedModeDiffers: 'Saved mode differs from current Trade Mode. Save schedule to sync.',
    backgroundAutoRun: 'Background Auto Run',
    stepLabel: 'Step',
    progressLabel: 'Progress',
    lastBackgroundScan: 'Last Background Scan',
    scannedLabel: 'scanned',
    filteredLabel: 'filtered',
    aiSuccessLabel: 'AI Success',
    nextAutoRun: 'Next auto run',
    waitingForNextMarketOpen: 'Waiting for next market open',
    stoppedForToday: 'Stopped for today — resumes next market open',
    noTradingToday: 'No trading today — resumes next trading day',
    nextMarketSchedule: 'Next 15 Days Market Schedule',
    recentAutoRuns: 'Recent Auto Runs',
    noScheduleData: 'No schedule data available.',
    // Execution columns
    time: 'Time',
    side: 'Side',
    qty: 'Qty',
    mode: 'Mode',
    orderId: 'Order ID',
    orderStatus: 'Status',
    // Common
    loading: 'Loading...',
    noData: 'No data',
    noResults: 'No results',
    configureFirst: 'Configure API key in Settings first',
    pleaseSignIn: 'Please sign in',
    connectionFailed: 'Connection failed',
    retry: 'Retry',
    refresh: 'Refresh',
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    close: 'Close',
    details: 'Details',
    view: 'View',
    hide: 'Hide',
    expand: 'Expand',
    collapse: 'Collapse',
    running: 'Running',
    stopped: 'Stopped',
    done: 'Done',
    success: 'Success',
    warning: 'Warning',
    // Status enums
    buyAction: 'BUY',
    watchAction: 'WATCH',
    skipAction: 'SKIP',
    continueAction: 'CONTINUE',
    low: 'LOW',
    medium: 'MEDIUM',
    high: 'HIGH',
    // Table columns
    colSymbol: 'Symbol',
    colDecision: 'Decision',
    colWhy: 'Why',
    blockLabel: 'Block',
    warnLabel: 'Warn',
    colStrategies: 'Strategies',
    colLiquidity: 'Liquidity',
    colValidation: 'Validation',
    colWhyMatched: 'Why Matched',
    colGrade: 'Grade',
    colRank: 'Rank',
    colSource: 'Source',
    colReturn1Y: '1Y Return',
    colSharpe: 'Sharpe',
    colMaxDD: 'Max DD',
    colWinRate: 'Win Rate',
    colPFactor: 'P.Factor',
    colTrades: 'Trades',
    colParams: 'Parameters',
    colLabel: 'Label',
    colStability: 'Stability',
    colTrend: 'Trend',
    colVerdict: 'Verdict',
    colRiskGate: 'Risk Gate',
    colAnalysisReason: 'Analysis Reason',
    colSrc: 'Src',
    colCurrent: 'Current',
    colEntryZone: 'Entry Zone',
    colStop: 'Stop',
    colTargets: 'Targets',
    colPosition: 'Position',
    colAIDecision: 'AI Decision',
    colGate: 'Gate',
    colFinalAction: 'Final Action',
    colData: 'Data',
    colPrice: 'Price',
    colChange: 'Change',
    colNews: 'News / Sentiment',
    colDataQual: 'Data Qual.',
    colScore: 'Score',
    colRisk: 'Risk',
    colPriceChg: 'Price / Chg',
    colSelectionReason: 'Selection Reason',
    // Alpaca
    alpacaMarketDataAPI: 'Alpaca Market Data API',
    internalBacktestEngine: 'Internal Backtest Engine',
    internalOptimizationEngine: 'Internal Optimization Engine',
    deterministicRules: 'Deterministic Rules',
    ruleBasedRiskGate: 'Rule-based risk gate',
    // Modal/Confirm
    confirmLiveTrading: 'Type "{expected}" to confirm live trading',
    confirmAction: 'Type "{expected}" to confirm',
    cleared: 'Watchlist cleared',
    addedToExecution: '{symbol} added to AI Execution.',
    removedFromWatchlist: '{symbol} removed from watchlist',
    alreadyInExecution: '{symbol} is already in the execution list.',
    noSymbol: 'No symbol.',
    // Time ranges
    oneDay: '1 Day',
    oneWeek: '1 Week',
    oneMonth: '1 Month',
    oneYear: '1 Year',
    all: 'All',
    changeLabel: 'Change',
    // AI config
    aiConfigLoadFailed: 'AI config load failed, keeping existing config',
    stoppingScanner: 'Stopping scanner...',
    // Order
    orderCanceled: 'Order canceled',
    orderCancelFailed: 'Cancel failed',
    orderFailed: 'Order failed',
    noOrderId: 'No order ID found.',
    alreadyFilled: 'Order already filled. Position moved to Current Holdings.',
    // Continue scan AI
    selectedReason: 'Selected{sectorText}: {nameOrSymbol} has {trendDesc}, {newsDesc} news sentiment, {riskDesc} risk, and {volDesc} volume{priceText}. Fine Scan should verify setup quality and backtest alignment before entry planning.',
    // Pipeline messages
    autoExecInfo: 'Auto-execution: {submitted} submitted, {failed} failed out of {total} candidates.',
    pipelineCompleteMsg: 'Pipeline complete! {execCount} execution candidates, {watchCount} added to watchlist{exitMsg}.',
    entryPlanPartial: 'Entry Plan partial: some symbols had errors ({symbols})',
    aiModePaperDesc: 'AI Mode + Paper: Qualified BUY plans are auto-submitted to Paper Trading. Blocked plans remain for review.',
    aiModeLiveDesc: 'AI Mode + Live: Qualified BUY plans are auto-submitted to Live Trading. Blocked plans remain for review.',
    hybridModeDesc: 'Hybrid Mode: Candidates are prepared for your review and manual submission.',
    manualModeDesc: 'Manual Mode: Stage candidates from Watchlist and submit orders manually.',
    symbolAnalysis: 'Symbol Analysis',
    epDecisionSetup: 'Decision & Setup',
    epEntryExitLevels: 'Entry / Exit Levels',
    epRiskData: 'Risk & Data',
    epAnalysisWarnings: 'Analysis & Warnings',
    finalAction: 'Final Action',
    aiDecision: 'AI Decision',
    readiness: 'Readiness',
    setupType: 'Setup Type',
    nextTacticalStep: 'Next Tactical Step',
    takeProfit1: 'Take Profit 1',
    takeProfit2: 'Take Profit 2',
    riskReward1: 'Risk/Reward 1',
    riskReward2: 'Risk/Reward 2',
    portfolioRisk: 'Portfolio Risk',
    technicalScore: 'Technical Score',
    regimeFilter: 'Regime Filter',
    reasoning: 'REASONING',
    riskNotes: 'RISK NOTES',
    blockers: 'BLOCKERS',
    statusReady: 'Ready',
    statusWaitingEntry: 'Waiting Entry',
    statusBlocked: 'Blocked',
    statusWatchOnly: 'Watch-only',
    statusStale: 'Stale',
    statusReview: 'REVIEW',
    statusGood: 'GOOD',
    statusNA: 'N/A',
    // Preflight
    sessionExpired: 'Session expired',
    alpacaNotConfigured: 'Alpaca Market Data API is not configured.',
    // Positions table
    asset: 'Asset',
    marketValue: 'Market Value',
    avgEntry: 'Avg Entry',
    plDollar: 'P/L ($)',
    plPercent: 'P/L (%)',
    longSide: 'LONG',
    shortSide: 'SHORT',
    // Portfolio
    portfolio: 'Portfolio',
    accountOverview: 'Account overview, positions, and performance',
    accountSnapshot: 'Account Snapshot',
    portfolioPerformance: 'Portfolio Performance',
    noPortfolioHistory: 'No portfolio history available',
    accountDetails: 'Account Details',
    accountId: 'Account ID',
    currency: 'Currency',
    noOpenPositions: 'No open positions',
    // Provenance labels
    marketSourceLabel: 'Market: ',
    backtestLabel: 'Backtest: Internal',
    optLabel: 'Opt: Internal',
    verdictLabel: 'Verdict: Rule-based',
    riskGateLabel: 'Risk Gate: ',
    decisionLabel: 'Decision',
    explainLabel: 'Explain',
    // Scanner status
    idle: 'idle',
    // Alpaca tag
    alpaca: 'ALPACA',
    // Execution table
    typeTif: 'Type / TIF',
    market: 'Market',
    limit: 'Limit',
    stopOrder: 'Stop',
    stopLimit: 'Stop Limit',
    trailStop: 'Trail Stop',
    day: 'Day',
    gtc: 'GTC',
    draft: 'DRAFT',
    cancelOrder: 'Cancel Order',
    cancelBeforeRemoving: 'Cancel before removing',
    retryOrder: 'Retry',
    removeOrder: 'Remove',
    reviewOnly: 'REVIEW ONLY',
    submitPaper: 'Submit Paper',
    confirmLive: 'Confirm Live',
    // Order confirmation modal
    confirmRealOrder: 'Confirm Real Order',
    confirmPaperOrder: 'Confirm Paper Order',
    entryPlanRecommends: 'Entry Plan recommends',
    shares: 'shares',
    buyBy: 'Buy by',
    dollarAmount: 'Dollar Amount',
    orderType: 'Order Type',
    trailingStop: 'Trailing Stop',
    limitPrice: 'Limit Price',
    stopPrice: 'Stop Price',
    trailPrice: 'Trail Price',
    trailPercent: 'Trail Percent',
    timeInForce: 'Time in Force',
    gtcLabel: 'GTC (Good Till Canceled)',
    iocLabel: 'IOC (Immediate or Cancel)',
    fokLabel: 'FOK (Fill or Kill)',
    estCost: 'Est. Cost',
    realTradingWarning: 'Real trading — this will place a real order with real money.',
    confirmRealOrderPlaceholder: 'Type: CONFIRM REAL ORDER {symbol}',
    submitRealOrder: 'Submit Real Order',
    submitPaperOrder: 'Submit Paper Order',
    keepOrder: 'Keep Order',
    cancelOrderConfirm: 'Cancel this order?',
    // AI Watchlist
    activeEntryMonitoring: 'Active entry monitoring',
    searchSymbol: 'Search symbol...',
    noAIWatchlistCandidates: 'No AI watchlist candidates yet',
    addFromEntryPlan: 'Add candidates from Entry Plan to monitor entry conditions autonomously.',
    total: 'Total',
    waitingEntry: 'Waiting Entry',
    reviewRequired: 'Review Required',
    readyOrHot: 'Ready / Hot',
    alpacaRealTimeFeed: 'ALPACA REAL-TIME FEED',
    chgPercent: 'Chg%',
    stopTarget: 'Stop / Target',
    addToExecution: 'Add to Execution Queue',
    alreadyInExecutionTooltip: 'Already in Execution',
    removeFromWatchlistTooltip: 'Remove from Watchlist',
    buyThisStock: 'Buy this stock',
    // Market Scanner
    scanningInProgress: 'Scanning in Progress',
    scanStopped: 'Scan Stopped',
    scanCompleted: 'Scan Completed',
    scanError: 'Scan Error',
    waitingForNextScan: 'Waiting for Next Scan',
    complete: 'Complete',
    symbolsProcessed: 'Symbols Processed',
    validated: 'Validated',
    retries: 'Retries',
    lastFailure: 'Last failure',
    currentlyScanning: 'Currently Scanning',
    marketScanSummary: 'Market Scan Summary',
    fullMarketScan: 'Full Market Scan',
    notScanned: 'Not scanned',
    universeScanned: 'Universe Scanned',
    bullish: 'Bullish',
    bearish: 'Bearish',
    neutral: 'Neutral',
    strongTrend: 'Strong Trend',
    newsRisk: 'News Risk',
    topMarketTrends: 'Top Market Trends',
    strong: 'Strong',
    dataQuality: 'Data Quality',
    goodData: 'Good',
    partialData: 'Partial',
    notConfiguredAI: 'Not configured',
    collecting: 'Collecting...',
    noDataYet: 'No data yet',
    localRules: 'Local Rules',
    ruleBasedOnly: 'Rule-based only',
    noAnalysisAvailable: 'No analysis available',
    noMarketScanResults: 'No market scan results yet',
    clickRunScanner: 'Click "Run Scanner" to start scanning the market',
    // Market Scanner - status labels
    statusScanning: 'SCANNING',
    statusCompleted: 'COMPLETED',
    statusError: 'ERROR',
    statusStopping: 'STOPPING',
    statusStopped2: 'STOPPED',
    statusIdle: 'IDLE',
    statusRunning: 'RUNNING',
    statusScannerReady: 'READY',
    // Market Scanner - progress
    progressSymbols: 'symbols',
    progressCompletedPrefix: 'Completed',
    progressStoppedAtPrefix: 'Stopped at',
    progressIdleText: 'Idle',
    // Market Scanner - continue scan labels
    sourceLabel: 'Source',
    selectionLabel: 'Selection',
    poolLabel: 'Pool',
    symbolsLabel: 'symbols',
    // Market Scanner - detail panel
    lastAnalysis: 'Last Analysis',
    analysisSource: 'Analysis Source',
    localRulesLabel: 'Local Rules',
    localLabel: 'Local',
    trendLabel: 'Trend',
    marketBasicInfo: 'Market & Basic Info',
    currentPrice: 'Current Price',
    change24h: '24h Change',
    dayLow: 'Day Low',
    dayHigh: 'Day High',
    volumeLiquidity: 'Volume & Liquidity',
    sectorLabel: 'Sector',
    industryLabel: 'Industry',
    dataProvenances: 'Data Provenances',
    marketDataLabel: 'Market Data',
    fundamentalsLabel: 'Fundamentals',
    sentimentLabel: 'Sentiment',
    detailedSourceUnavailable: 'Detailed source mapping unavailable',
    analysisScores: 'Analysis & Scores',
    overallTrendScore: 'Overall Trend Score',
    confidenceLevel: 'Confidence Level',
    dimensionalBreakdown: 'Dimensional Breakdown',
    dimTrend: 'Trend',
    dimMomentum: 'Momentum',
    dimVolume: 'Volume',
    dimVolatility: 'Volatility',
    dimStructure: 'Structure',
    dimSentiment: 'Sentiment',
    riskPrefix: 'RISK',
    volPrefix: 'VOL',
    volHigh: 'HIGH',
    volStable: 'STABLE',
    aiAgentReasoning: 'AI Agent Reasoning',
    detailedAnalysis: 'Detailed Analysis',
    latestMarketNews: 'Latest Market News',
    noSymbolNews: 'No symbol-specific news found in the last 24h.',
    newsConsistent: 'Recent news headline identified. Sentiment remains consistent with the current trend score.',
    agentRecommendation: 'Agent Recommendation',
    recommendedNextStep: 'Recommended Next Step',
    strategicReasoning: 'AI Agent Strategic Reasoning',
    compilingReasoning: 'AI agent is compiling strategic reasoning for this candidate...',
    noDetailedAnalysis: 'No detailed analysis available for this symbol.',
    unknownCompany: 'Unknown Company',
    // Fine Scan detail panel
    matchSummary: 'Match Summary',
    strategyBacktest: 'Strategy Backtest Validation',
    quickOptimization: 'Quick Parameter Optimization',
    surgicalEntry: 'Surgical Entry Analysis',
    highConvictionSignals: 'High-Conviction Signals',
    liquidityRiskProfile: 'Liquidity & Risk Profile',
    newsAndCatalyst: 'News & Market Catalyst',
    evaluationPeriod: 'Evaluation Period',
    backtestNotAvailable: 'Backtest data not yet available for this candidate.',
    optimizationNotAvailable: 'Optimization not executed for this candidate.',
    optimizationInProgress: 'Optimization in progress...',
    entryQualityUnavailable: 'Entry quality metrics currently unavailable.',
    setupScore: 'Setup Score',
    noSignificantNews: 'No significant recent news catalysts detected.',
    newsSentimentUnavailable: 'News sentiment analysis unavailable.',
    viewMoreHeadlines: 'View {count} more headlines in terminal...',
    noDataLabel: 'No data',
    // Market Scanner - content labels
    sentimentPositive: 'Positive',
    sentimentNegative: 'Negative',
    sentimentMixed: 'Mixed',
    riskHigh: 'High',
    riskMedium: 'Medium',
    riskLow: 'Low',
    volumeHigh: 'High',
    volumeNormal: 'Normal',
    volumeLow: 'Low',
    trendStrongBullish: 'Strong Bullish',
    trendBullish: 'Bullish',
    trendNeutral: 'Neutral',
    trendBearish: 'Bearish',
    trendStrongBearish: 'Strong Bearish',
    // Continue Scan
    selectionSuccessful: 'Selection Successful',
    foundTopCandidates: 'Found {count} top-tier candidates for follow-up analysis.',
    avgPriority: 'Avg Priority',
    riskMix: 'Risk Mix (L/M/H)',
    avgScore: 'Avg Score',
    aiCoverage: 'AI Coverage',
    aiRules: 'AI + RULES',
    rulesOnly: 'RULES ONLY',
    source: 'Source',
    pool: 'Pool',
    lastSync: 'Last Sync',
    scoreBreakdown: 'Score Breakdown',
    weight: 'Weight',
    selectionDisclosure: 'Selection Disclosure: These candidates are filtered from the primary scanner and priority-weighted. Run Fine Scan to validate strategy fit and backtest quality.',
    noMarketScanResultsAvailable: 'No market scan results available',
    marketScanInProgress: 'Market scan in progress...',
    scanStoppedBeforeResults: 'Scan was stopped before any results were collected.',
    runMarketScannerFirst: 'Run Market Scanner first to build a continue scan shortlist.',
    ruleBasedScanInProgress: 'Rule-based scan in progress...',
    processingNCandidates: 'Processing {processed} of {total} candidates',
    noSuitableCandidates: 'No suitable candidates found',
    noBullishCandidatesMet: 'Market scan completed but no bullish candidates met the criteria for continue scan',
    continueScanFailed: 'Continue scan processing failed',
    continueScanError: 'An error occurred while processing continue scan candidates',
    readyToStartContinueScan: 'Ready to start continue scan',
    builtFromPartialResults: 'Built from partial scanner results: {processed}/{total} symbols',
    marketScanCompletedResults: 'Market scan completed with {count} results',
    clickStartContinueScan: 'Click "Start Continue Scan" to run rule-based selection on current results',
    startSelection: 'Start Selection',
    processing: 'Processing...',
    displaying: 'Displaying',
    of: 'of',
    candidatesWord: 'candidates',
    // Fine Scan
    scanningRegimeStrategies: 'Scanning Regime & Strategies',
    fineScanInterrupted: 'Scan Interrupted',
    processProgress: 'Process Progress',
    retained: 'Retained',
    candidatesLabel: 'Candidates:',
    continueLabel: 'Continue:',
    watchLabel: 'Watch:',
    rejectLabel: 'Reject:',
    needDataLabel: 'Need Data:',
    aiAgentLabel: 'AI Agent:',
    systemReady: 'System ready for multi-dimensional strategy confirmation',
    awaitingInput: 'Awaiting Market Scanner candidates',
    runFineScan: 'Run Fine Scan',
    fineScanRunning: 'Running...',
    excellent: 'Excellent',
    wait: 'Wait',
    nearRes: 'Near Res',
    poorRR: 'Poor R/R',
    fineScanNoData: 'No Data',
    backtestLabelShort: 'Backtest: ',
    positive: 'Positive',
    negative: 'Negative',
    caution: 'Caution',
    optLabelShort: 'Opt: ',
    fineScanStable: 'Stable',
    weak: 'Weak',
    overfitRisk: 'Overfit Risk',
    overfit: 'Overfit',
    whyMatchedTooltip: 'AI-generated explanation',
    whyMatchedTemplate: 'Template-based (market data)',
    noCandidatesToAnalyze: 'No candidates to analyze. Run Market Scanner first.',
    errorDuringFineScan: 'An error occurred during Fine Scan',
    runFineScanToMatch: 'Run Fine Scan to match strategies for continue-list candidates',
    step1Regime: 'Step 1: Regime & strategy matching',
    step3QuickBacktest: 'Step 3: Quick backtest validation',
    // Deeper Validation
    validating: 'Validating...',
    validatingLabel: 'VALIDATING',
    interruptedLabel: 'INTERRUPTED',
    riskGatePass: 'RISK GATE PASS',
    confirmed: 'CONFIRMED',
    systemMonteCarlo: 'SYSTEM',
    monteCarloV2: 'MONTE CARLO v2',
    readyForHistorical: 'Ready for historical stress testing.',
    candidatesAvailable: 'candidates available.',
    systemPerformingValidation: 'System performing multi-regime risk validation...',
    runningDeeperValidation: 'Running Deeper Validation',
    backtestOptStability: 'Backtest, optimization & stability analysis — can take 1–3 minutes for multiple symbols',
    validationFailed: 'Validation failed',
    checkBackendLogs: 'Please check backend logs or retry.',
    failedSymbols: 'Failed symbols:',
    candidatesFromFineScan: 'candidates from Fine Scan ready for deep validation.',
    continuePlusWatch: 'Continue + Watch-to-Validate',
    scoreGe50: 'score ≥ 50, no critical blockers',
    completeFineScanFirst: 'Complete Fine Scan to enable deeper historical validation.',
    // Entry Plan
    epGenerating: 'Generating...',
    generatingLabel: 'GENERATING',
    interruptedLabel2: 'INTERRUPTED',
    runEntryPlan: 'Run Entry Plan',
    noValidatedCandidatesYet: 'No validated candidates yet. Run Deeper Validation first.',
    noConfirmedOrWatch: 'No Confirmed or Watch candidates available from Deeper Validation.',
    validatedCandidatesReady: 'validated candidates ready. Click',
    toGenerateEntryPlans: 'to generate entry plans.',
    computingEntryZones: 'Computing entry zones, stop loss, position sizes, and risk gates...',
    entryPlanFailed: 'Entry plan generation failed. Please try again.',
    aiDecisions: 'AI Decisions',
    buyLabel: 'Buy',
    epWatchLabel: 'Watch',
    skipLabel: 'Skip',
    localRulesNoAICall: 'Local Rules (no AI call)',
    riskReview: 'Risk Review',
    passed: 'Passed',
    review: 'Review',
    blocked: 'Blocked',
    tradeReadiness: 'Trade Readiness',
    ready: 'Ready',
    waitLabel: 'Wait',
    blockedLabel: 'Blocked',
    execution: 'Execution',
    aiModePaperReady: 'AI Mode · Paper Execution Ready',
    aiModeLiveConfirm: 'AI Mode · Auto-Execute Ready',
    hybridModeManual: 'Hybrid Mode · Manual Execution',
    manualModeRecommendations: 'Manual Mode · Recommendations Only',
    paperPreviewOnly: 'Auto-submit orders',
    manualConfirmRequired: 'Auto-submit orders',
    noAutomaticOrders: 'No automatic orders',
    manualOnly: 'Manual only',
    realTradingRequiresConfirm: 'Real trading requires manual confirmation. No order is placed automatically.',
    executionPlan: 'A. Execution Plan',
    positionRisk: 'B. Position & Risk',
    decision: 'C. Decision',
    dataQualityCard: 'D. Data Quality',
    epCurrentPrice: 'Current Price',
    distance: 'Distance',
    epTrigger: 'Trigger',
    epTargets: 'Targets',
    epInvalidation: 'Invalidation',
    orderTypeSuggestion: 'Order Type',
    epPortfolio: 'Portfolio',
    epBuyingPower: 'Buying Power',
    riskBudget: 'Risk Budget',
    actualRisk: 'Actual Risk',
    riskUsed: 'Risk Used',
    ofRiskBudget: 'of risk budget',
    size: 'Size',
    estValue: 'Est. Value',
    capStatus: 'Cap Status',
    cappedAt: 'Capped at',
    okAt: 'OK ({pct} of equity)',
    epAIDecision: 'AI Decision',
    epConfidence: 'Confidence',
    epRiskGate: 'Risk Gate',
    tradeReadinessLabel: 'Trade Readiness',
    entryTrigger: 'Entry Trigger',
    readyOrWaiting: 'Ready',
    waiting: 'Waiting',
    bestStrategy: 'Best Strategy',
    accountData: 'Account Data',
    aiProviderLabel: 'AI Provider',
    localRulesFallback: 'Local Rules fallback',
    noAIProviderConfigured: 'No AI provider configured or call failed',
    broker: 'Broker',
    notConnected: 'Not Connected',
    aiErrorLabel: 'AI Error',
    decisionReason: 'Decision Reason',
    noDetailedReasoning: 'No detailed reasoning provided.',
    nextStep: 'Next Step',
    waitForFurtherSignals: 'Wait for further signals.',
    riskComment: 'Risk Comment',
    riskAssessment: 'Risk Assessment',
    noRiskWarnings: 'No risk warnings identified.',
    criticalBlockers: 'Critical Blockers',
    epExecuteTrade: 'Execute Trade',
    reviewAndExecute: 'Review & Execute',
    epMonitorEntry: 'Monitor Entry',
    planSkipped: 'Plan Skipped',
    epRiskBlocked: 'Risk Blocked',
    inWatchlist: 'In Watchlist',
    inZone: 'IN ZONE',
    fromEntry: 'from entry',
    pullback: 'Pullback',
    breakout: 'Breakout',
    support: 'Support',
    localRulesTooltip: 'Local Rules — no LLM call. AI provider not configured or call failed.',
    execute: 'Execute',
    skipped: 'Skipped',
    update: 'Update',
    plusWatchlist: '+ Watchlist',
    // Exit Scan
    evaluateHoldings: 'Evaluate current holdings and open sell plans for exit readiness.',
    scanned: 'Scanned',
    sellNow: 'Sell Now',
    targetLimit: 'Target Limit',
    hold: 'Hold',
    epPending: 'Pending',
    epSubmitted: 'Submitted',
    runExitScan: 'Run Exit Scan',
    reRunExitScan: 'Re-run Exit Scan',
    stopScanner: 'Stop',
    noExitScanResults: 'No exit scan results',
    clickRunExitScan: 'Click "Run Exit Scan" to analyze current holdings for exit opportunities.',
    scanningHoldings: 'Scanning holdings...',
    noHoldingsRequireReview: 'No holdings require exit review',
    exitScanEvaluates: 'Exit Scan evaluates current positions and existing sell orders.',
    clearResults: 'Clear Results',
    noActiveHoldings: 'No active holdings to scan',
    exitPrice: 'Exit Price',
    sellNowLabel: 'SELL NOW',
    targetLimitLabel: 'TARGET LIMIT',
    holdLabel: 'HOLD',
    manualReviewLabel: 'REVIEW',
    blockedLabelExit: 'BLOCKED',
    mktSell: 'MKT SELL',
    lmtSell: 'LMT SELL',
    noOrder: 'NO ORDER',
    // Execution confirmation modal
    liveTradingConfirmation: 'Live Trading Confirmation',
    confirmOrderExecution: 'Confirm Order Execution',
    liveTradingUsesRealMoney: 'Live trading uses real money. Confirm before submitting.',
    confirmLiveOrder: 'Confirm Live Order',
    submitOrder: 'Submit Order',
    symbolLabel: 'Symbol',
    setupLabel: 'Setup',
    orderTypeLabel: 'Order Type',
    sharesLabel: 'Shares',
    entryZoneLabel: 'Entry Zone',
    estimatedValue: 'Estimated Value',
    maxRisk: 'Max Risk',
    stopLossLabel: 'Stop Loss',
    takeProfitLabel: 'Take Profit',
    modeLabel: 'Mode',
    confirmLiveBuy: 'Type {text} to confirm:',
    belowEntry: 'Below entry by ${diff} ({pct}%)', // eslint-disable-line no-template-curly-in-string
    aboveEntry: 'Above entry by ${diff} ({pct}%)', // eslint-disable-line no-template-curly-in-string
    inEntryZone: 'In entry zone',
    noReasonProvided: 'No reason provided',
    invalidationLabel: 'Invalidation:',
    colSetup: 'Setup',
    colQty: 'Qty',
    colAvgEntry: 'Avg Entry',
    colPL: 'P/L',
    colPLPct: 'P/L %',
    colExitDecision: 'Decision',
    colExitOrder: 'Order',
    colExitStatus: 'Status',
    reviewLabel: 'REVIEW',
    epNoDetailedAnalysis: 'No detailed analysis provided',
    filledLabel: 'FILLED',
    failedLabel: 'FAILED',
    scanningLabel: 'SCANNING',
    completedLabel: 'COMPLETED',
    skippedLabel: 'SKIPPED',
    errorLabel: 'ERROR',
    stoppedLabel: 'STOPPED',
    epLocalRules: 'Local Rules',
    colTarget: 'Target',
    unknown: 'Unknown',
    // Fine Scan detail - table headers
    btColStrategy: 'Strategy',
    btColReturn: 'Return',
    btColSharpe: 'Sharpe',
    btColMaxDD: 'MaxDD',
    btColWinPct: 'Win%',
    btColTrades: 'Trades',
    btColStatus: 'Status',
    optColStrategy: 'Strategy',
    optColStability: 'Stability',
    optColAvgReturn: 'Avg Return',
    optColPosRatio: 'Pos Ratio',
    optColSpread: 'Spread',
    // Fine Scan detail - status tags
    statusPass: 'PASS',
    statusCaution: 'CAUT',
    statusFail: 'FAIL',
    statusStable: 'STABLE',
    statusWeak: 'WEAK',
    statusOverfit: 'OVERFIT',
    // Fine Scan detail - prefix labels
    scorePrefix: 'SCORE ',
    blockPrefix: 'BLOCK: ',
    dataPrefix: 'DATA: ',
    limitedSampleTag: 'LIMITED SAMPLE',
    localRulesTag: 'LOCAL RULES',
    deepseekTag: 'DEEPSEEK V4 FLASH',
    // Fine Scan detail - provenance chips
    provenanceMkt: 'MARKET SCANNER',
    provenanceBt: 'BACKTEST ENGINE',
    provenanceOpt: 'OPTIMIZER',
    provenanceEntry: 'ENTRY ANALYSIS',
    provenanceNews: 'NEWS AGGREGATOR',
    provenanceDecision: 'DECISION: ',
    provenanceAI: 'AI',
    provenanceRules: 'RULES',
    // Fine Scan detail - liquidity/risk tags
    liquidityLabel: 'Liquidity',
    tagGrade: 'Grade',
    tagRvol: 'RVOL',
    tagSpread: 'Spread',
    tagScore: 'Score',
    tagAtr: 'ATR',
    tagPrice: 'Price',
    tagSupport: 'Support',
    tagResistance: 'Resistance',
    tagZone: 'Zone',
    tagStop: 'Stop',
    tagRR: 'R/R',
    // Fine Scan detail - news tags
    tagSentiment: 'Sentiment',
    tagHeadlines: 'Headlines',
    tagEarnings: 'Earnings',
    earningsUpcoming: 'Upcoming',
    earningsClear: 'Clear',
    // Fine Scan detail - AI reasoning
    tagSource: 'Source',
    tagTokens: 'Tokens',
    tokensOptimized: 'Optimized',
    rrCapped: 'R/R Capped: ',
    // Fine Scan detail - regime
    regimeLabel: 'Regime',
    regimeTrending: 'Trending',
    regimeBreakout: 'Breakout-ready',
    regimeRange: 'Range-bound',
    regimeUnclear: 'Unclear',
    // Fine Scan detail - liquidity sub label
    liquiditySubLabel: 'Liquidity',
    // Fine Scan - supplementary
    partialLabel: 'Partial',
    processingMarketData: 'Processing market data for selected candidates...',
    // Deeper Validation detail - supplementary
    naLabel: 'N/A',
    ruleBasedLabel: 'Rule-based',
    gatePrefix: 'GATE',
    gateBlocked: 'Blocked',
    paramRet: 'ret',
    paramSharpe: 'sharpe',
    dvTrendDivergent: 'Divergent',
    dvReasonConfirmed: 'Confirmed',
    dvReasonWatch: 'Watch',
    dvReasonReject: 'Reject',
    dvReasonRecentWeakening: 'recent Weakening',
    dvReasonParamProfitable: 'parameter sets profitable',
    dvReasonMedianReturn: 'median return',
    dvReasonMedianSharpe: 'median sharpe',
    dvReasonProfitFactor: 'profit factor',
    dvReasonSharpe: 'sharpe',
    dvReasonReturn: 'return',
    dvReasonDrawdown: 'drawdown',
    dvReasonWinRate: 'win rate',
    dvReasonTrades: 'trades',
    dvReasonSampleLimited: 'Sample Limited',
    dvReasonStabilityReason: 'Stability assessment',
  },
  trade: {
    title: 'Trading Workstation',
    subtitle: 'Monitor account equity, manage positions, and place orders directly to Alpaca.',
    paperTrading: 'PAPER TRADING',
    liveTrading: 'LIVE TRADING',
    refreshData: 'Refresh Data',
    autoRefreshOn: 'AUTO REFRESH: ON',
    autoRefreshOff: 'AUTO REFRESH: OFF',
    updated: 'Updated',
    realTimeData: 'REAL-TIME DATA',
    realModeTitle: 'Real Trading Mode',
    realModeDesc: 'Live Alpaca account data active. Orders will execute with real funds.',
    watchlistTitle: 'AI Entry Watchlist',
    watchlistSubtitle: 'Autonomous symbol monitoring',
    refresh: 'Refresh',
    noWatchlist: 'No AI watchlist candidates',
    noWatchlistDesc: 'Candidates from Entry Plan will appear here for monitoring.',
    statActive: 'Active',
    statReadySoon: 'Ready Soon',
    statReview: 'Review',
    statArchived: 'Archived',
    colSymbol: 'Symbol',
    colSetup: 'Setup',
    colAI: 'AI',
    colCnf: 'Cnf',
    colEntryZone: 'Entry Zone',
    colStop: 'Stop',
    colTarget: 'Target',
    colRR: 'R/R',
    colGate: 'Gate',
    colFinal: 'Final',
    colNextStep: 'Next Tactical Step',
    buy: 'Buy',
    remove: 'Remove',
    finalBuyReady: 'BUY READY',
    finalWaitEntry: 'WAIT ENTRY',
    finalSkip: 'SKIP',
    finalBlocked: 'BLOCKED',
    accountSnapshot: 'Account Snapshot',
    newOrder: 'New Order',
    reviewOrder: 'Review Order',
    finalConfirmation: 'Final Confirmation',
    confirmPlaceReal: 'Confirm & Place Real Order',
    placeRealOrder: 'Place Real Order',
    placePaperOrder: 'Place Paper Order',
    paperTradingLabel: 'PAPER TRADING',
    realTradingLabel: 'REAL TRADING',
    cash: 'Cash',
    equity: 'Equity',
    buyingPower: 'Buying Power',
    portfolioValue: 'Portfolio Value',
    accountLabel: 'Account',
    statusLabel: 'Status',
    positionsLabel: 'Positions',
    openOrdersLabel: 'Open Orders',
    portfolioPerformance: 'Portfolio Performance',
    periodChange: 'Period Change',
    portfolioValueLabel: 'Portfolio Value',
    periodOpen: 'Period Open',
    periodHigh: 'Period High',
    periodLow: 'Period Low',
    maxDrawdown: 'Max Drawdown',
    drawdown: 'Drawdown',
    openingBaseline: 'Opening Baseline',
    observations: 'observations',
    easternTime: 'Eastern Time',
    loadingPortfolio: 'Loading portfolio data...',
    portfolioUnavailable: 'Portfolio history is unavailable.',
    portfolioUnavailableDesc: 'Check Alpaca credentials and connection.',
    noPortfolioHistory: 'No portfolio history available',
    noPortfolioHistoryDesc: 'Alpaca returned no data for the selected period.',
    range1Day: '1 Day',
    range1Week: '1 Week',
    range1Month: '1 Month',
    range3Months: '3 Months',
    range1Year: '1 Year',
    rangeAll: 'All',
    positions: 'Positions',
    noPositions: 'No open positions in this account.',
    openOrders: 'Open Orders',
    noOpenOrders: 'No active orders in this account.',
    orderHistory: 'Order History',
    noOrderHistory: 'No order history available.',
    colAsset: 'Asset',
    colPrice: 'Price',
    colQty: 'Qty',
    colSide: 'Side',
    colMarketValue: 'Market Value',
    colAvgEntry: 'Avg Entry',
    colCostBasis: 'Cost Basis',
    colTodayPLPct: "Today's P/L (%)",
    colTodayPL: "Today's P/L ($)",
    colTotalPLPct: 'Total P/L (%)',
    colTotalPL: 'Total P/L ($)',
    colType: 'Type',
    colLimitPrice: 'Limit Price',
    colStopPrice: 'Stop Price',
    colTIF: 'TIF',
    colCreated: 'Created',
    cancel: 'Cancel',
    colTime: 'Time',
    colAvgFillPrice: 'Avg Fill Price',
    colFilledQty: 'Filled Qty',
    invalidDate: 'Invalid Date',
    cancelOrderTitle: 'Cancel order for {symbol}?',
    cancelOrderLiveDesc: 'This will cancel an order on your LIVE Alpaca account. This action cannot be undone.',
    cancelOrderPaperDesc: 'This will cancel the order on your {mode} account.',
    cancelLiveOrder: 'Cancel LIVE Order',
    cancelOrder: 'Cancel Order',
    keepOrder: 'Keep Order',
    orderCancelled: 'Order cancelled successfully',
    cancelFailed: 'Cancel failed',
    orderPlaced: 'Order placed successfully',
    refreshFailed: 'Failed to refresh data',
    removeFailed: 'Failed to remove',
    paperLabel: 'Paper',
    liveLabel: 'Live',
    credentialsNotConfigured: '{mode} trading credentials not configured. Please save API keys in Settings.',
    sell: 'Sell',
  },
  portfolio: {
    title: 'Portfolio',
    subtitle: 'Account overview, positions, and performance',
    refresh: 'Refresh',
    accountSnapshot: 'Account Snapshot',
    cash: 'Cash',
    equity: 'Equity',
    buyingPower: 'Buying Power',
    portfolioValue: 'Portfolio Value',
    accountLabel: 'Account',
    statusLabel: 'Status',
    positionsLabel: 'Positions',
    openOrdersLabel: 'Open Orders',
    portfolioPerformance: 'Portfolio Performance',
    change: 'Change',
    noPortfolioHistory: 'No portfolio history available',
    portfolioHistoryLoadFailed: 'Failed to load portfolio history',
    fallbackHistoryNote: 'Reference curve from current account equity',
    fallbackHistoryBanner: 'Estimated history displayed — connect Alpaca portfolio history for verified account equity.',
    verifiedAlpacaHistory: 'Verified Alpaca history',
    dataSourceAlpaca: 'Data source: Alpaca',
    range1Day: '1 Day',
    range1Week: '1 Week',
    range1Month: '1 Month',
    range3Months: '3 Months',
    range1Year: '1 Year',
    rangeAll: 'All',
    accountDetails: 'Account Details',
    accountID: 'Account ID',
    status: 'Status',
    currency: 'Currency',
    positions: 'Positions',
    noPositions: 'No open positions',
    colAsset: 'Asset',
    colPrice: 'Price',
    colQty: 'Qty',
    colSide: 'Side',
    colMarketValue: 'Market Value',
    colAvgEntry: 'Avg Entry',
    colPL: 'P/L ($)',
    colPLPct: 'P/L (%)',
    mode: 'Mode',
    currentPaperTrading: 'Current: Paper Trading',
    currentRealTrading: 'Current: Real Trading',
    paperTrading: 'Paper Trading',
    realTrading: 'Real Trading',
    sourcePaper: 'Alpaca Paper',
    sourceReal: 'Alpaca Real',
    apiErrorTitle: 'Portfolio data unavailable',
    configureAction: 'Open Configuration',
    dataUnavailable: 'Unable to load portfolio data from the backend.',
    todayPL: 'Today P/L',
    totalUnrealizedPL: 'Total Unrealized P/L',
    dayTradeBuyingPower: 'Day Trade Buying Power',
    maintenanceMargin: 'Maintenance Margin',
    holdings: 'Holdings',
    summary: 'Summary',
    accountStatus: 'Account Status',
    tradingMode: 'Trading Mode',
    dataSource: 'Data Source',
    lastUpdated: 'Last Updated',
    exposure: 'Exposure',
    riskStatus: 'Risk / Exposure',
    riskClear: 'Clear',
    tradingBlocked: 'Trading Blocked',
    accountBlocked: 'Account Blocked',
    patternDayTrader: 'Pattern Day Trader',
    realModeNote: 'Real trading mode uses your live Alpaca account data.',
    paperModeNote: 'Paper trading mode uses your simulated Alpaca account data.',
    noPortfolioHistoryDesc: 'Portfolio history will appear after Alpaca returns account history data.',
    portfolioValueLine: 'Portfolio Value',
    profitLossLine: 'Profit / Loss',
    colCurrentPrice: 'Current Price',
    colCostBasis: 'Cost Basis',
    colTodayChange: 'Today Change',
    tooltipTime: 'Time',
    tooltipEquity: 'Total Equity',
    tooltipChange: 'Change',
    tooltipChangePct: 'Change %',
  },
  settings: {
    title: 'Settings',
    subtitle: 'Manage connections, display preferences, and account access.',
    tradingProvider: 'Trading Provider',
    intelligence: 'Intelligence',
    marketData: 'Market Data',
    configured: 'Configured',
    notConfigured: 'Not Configured',
    checking: 'Checking...',
    aiProvider: 'AI Provider',
    platformManagement: 'Platform Management',
    connectionConfig: 'Connection Configuration',
    connectionConfigDesc: 'Set up and test your API keys for all external quantitative services.',
    securityAccount: 'Security & Account',
    accountInfo: 'Account Information',
    loggedInAs: 'Logged in as',
    securitySettings: 'Security Settings',
    signOut: 'Log Out',
    footerNote: 'Saved credentials are masked in the interface. Rotate a key at its provider if you suspect exposure.',
  },
  config: {
    title: 'Connections',
    subtitle: 'Save and test the services used by your research workspace.',
    backToSettings: 'Back to Settings',
    signOut: 'Sign Out',
    footerSecurity: 'Saved keys are masked after entry. Use the provider dashboard to rotate or revoke a credential.',
    pleaseSignIn: 'Please sign in before saving settings.',
    saveFailed: 'Save failed',
    connectionFailed: 'Connection failed',
    statusConnected: 'Connected',
    statusSaved: 'Saved',
    statusError: 'Error',
    statusNotTested: 'Not tested',
    paperTitle: 'Alpaca Paper Trading',
    paperSubtitle: 'Credentials for simulated trading environment.',
    paperSaved: 'Paper trading settings saved',
    paperTestOK: 'Paper connection OK — Account: {id}',
    apiKey: 'API Key',
    secretKey: 'Secret Key',
    endpointUrl: 'Endpoint URL',
    savedMasked: 'Saved (masked)',
    enterApiKey: 'Enter API key',
    enterSecretKey: 'Enter secret key',
    testConnection: 'Test Connection',
    saveChanges: 'Save Changes',
    paperSecurityNote: 'Paper keys are used for testing only. Real funds are never at risk here.',
    alpacaWebsite: 'Alpaca API website',
    finnhubWebsite: 'Finnhub API website',
    liveTitle: 'Alpaca Live Trading',
    liveSubtitle: 'Credentials used when you explicitly choose the live environment.',
    securityWarning: 'Security Warning',
    securityWarningDesc: 'Live trading involves real financial risk. Ensure your keys are kept secure and never shared.',
    liveSaved: 'Real trading settings saved',
    liveTestOK: 'Live connection OK — Account: {id}',
    testLiveConnection: 'Test Live Connection',
    saveLiveSettings: 'Save Live Settings',
    marketDataTitle: 'Alpaca Market Data',
    marketDataSubtitle: 'Endpoint for bars, snapshots, and streaming quotes.',
    keysAutoSynced: 'Keys auto-synced from Real Trading',
    configureRealFirst: 'Configure Real Trading first for automatic sync',
    dataEndpointUrl: 'Data Endpoint URL',
    dataFeedTier: 'Data Feed Tier',
    iexFree: 'IEX (Free Tier)',
    sipPaid: 'SIP (Paid Tier)',
    dataApiKey: 'Data API Key',
    dataApiSecret: 'Data API Secret',
    usingRealKey: 'Using Real Trading Key',
    usingRealSecret: 'Using Real Trading Secret',
    configureRealTrading: 'Configure Real Trading',
    testDataAccess: 'Test Data Access',
    saveConfiguration: 'Save Configuration',
    marketDataSaved: 'Market data settings saved',
    marketDataConnected: 'Market data connected (Source: {source})',
    aiTitle: 'Model provider',
    aiSubtitle: 'Provider and model used for research review.',
    keyMismatch: 'Key Mismatch',
    keyMismatchDesc: 'The saved key appears to be a masked value. Please re-enter your actual API key to restore service.',
    provider: 'Provider',
    primaryModel: 'Primary Model',
    customModel: 'Custom model...',
    modelIdentifier: 'Model Identifier',
    modelPlaceholder: 'e.g. gpt-4o or deepseek-v4-flash',
    apiKeyLabel: 'API Key',
    apiBaseUrl: 'API Base URL',
    testAiConnection: 'Test Connection',
    saveIntelligence: 'Save Intelligence Settings',
    aiSaved: 'AI settings saved',
    aiConnected: 'AI connection successful',
    finnhubTitle: 'Finnhub Financial Data',
    finnhubSubtitle: 'Provider for company fundamentals, sector data, and earnings.',
    finnhubApiKey: 'API Key',
    finnhubBaseUrl: 'Base API URL',
    testFinnhub: 'Test Finnhub',
    finnhubSaved: 'Finnhub settings saved',
    finnhubConnected: 'Finnhub connection verified',
  },
  auth: {
    createAccount: 'Create account',
    signUpTitle: 'Start with AlphaLab',
    signUpSubtitle: 'Set up a research workspace that starts in paper mode.',
    trustMicrocopy: 'Email verification · Masked credentials · Paper first',
    buttonTip: 'Complete verification to continue',
    fullName: 'Full name',
    fullNamePlaceholder: 'Enter your full name',
    emailAddress: 'Email address',
    emailPlaceholder: 'Use a valid email address',
    password: 'Password',
    passwordPlaceholder: '8+ characters with uppercase, lowercase, and a number',
    confirmPassword: 'Confirm password',
    confirmPasswordPlaceholder: 'Re-enter your password',
    termsPrefix: 'I agree to the',
    termsOfService: 'Terms of Service',
    privacyPolicy: 'Privacy Policy',
    and: 'and',
    createAccountBtn: 'Create Account',
    creatingAccount: 'Creating account...',
    alreadyHaveAccount: 'Already have an account?',
    signIn: 'Sign in',
    backToHome: 'Back to home',
    enterFullName: 'Please enter your full name',
    enterValidEmail: 'Please enter a valid email address',
    passwordMinLength: 'Use at least 8 characters with an uppercase letter, a lowercase letter, and a number',
    passwordPolicyError: 'Password must have at least 8 characters, including an uppercase letter, a lowercase letter, and a number',
    passwordRuleLength: '8+ characters',
    passwordRuleMatch: 'Passwords match',
    passwordRuleUpper: '1 uppercase letter',
    passwordRuleLower: '1 lowercase letter',
    passwordRuleNumber: '1 number',
    passwordsDoNotMatch: 'Passwords do not match',
    acceptTerms: 'Please agree to the Terms of Service and Privacy Policy',
    accountCreated: 'Check your account status',
    accountCreatedDesc: 'If this address can be registered and confirmation is required, you will receive an email. If you already have an account, sign in or reset your password.',
    goToSignIn: 'Continue to Sign In',
    accountReady: 'Your account is ready',
    accountReadyDesc: 'Email confirmation is not required for this account. Continue to your workspace.',
    continueToWorkspace: 'Continue to Workspace',
    resendConfirmation: 'Resend confirmation email',
    resendingConfirmation: 'Sending confirmation email...',
    confirmationResent: 'If confirmation is available for this address, you will receive an email. Use only the newest link.',
    confirmationResendCaptcha: 'Complete human verification to request another confirmation email.',
    confirmationResendRateLimit: 'Please wait a moment before requesting another confirmation email.',
    confirmationResendFailed: 'We could not send another confirmation email. Please try again shortly.',
    buildYourEdge: 'Set up your research workspace',
    featureAiTitle: 'Model-assisted review',
    featureBacktestTitle: 'Backtest before trading',
    featurePaperTitle: 'Paper-first execution',
    signUpHint: 'After signing up, configure your Alpaca, Finnhub, and AI provider keys in Settings.',
    nextStepsTitle: 'What happens next?',
    nextStep1: 'Verify your email',
    nextStep2: 'Connect providers when ready',
    nextStep3: 'Start with paper trading',
    oauthHint: 'Sign-in is handled by Supabase Auth.',
    oauthRedirecting: 'Redirecting...',
    oauthFailed: 'OAuth login failed. Please try again.',
    registrationFailed: 'Registration failed',
    resetFailed: 'Failed to send reset email. Please try again.',
    passwordUpdateFailed: 'Password update failed. Please try again.',
    sending: 'Sending...',
    updating: 'Updating...',
    signUpHelperAll: 'Please complete verification and accept terms to continue',
    passwordsDoNotMatchError: 'Passwords do not match',
    invalidCredentials: 'Invalid email or password',
    checkEmailConfirmation: 'Check your email to confirm your account.',
    welcomeBack: 'Welcome back',
    signInSubtitle: 'Access your AlphaLab workspace.',
    authFeatureMarketTitle: 'AI Market Research',
    authFeatureMarketDesc: 'Deterministic market scans with a separate model review layer.',
    authFeatureExecutionTitle: 'Paper-first Execution',
    authFeatureExecutionDesc: 'Test assumptions in simulation before connecting live capital.',
    authFeatureSecurityTitle: 'Protected Configs',
    authFeatureSecurityDesc: 'Provider credentials are masked and kept out of browser-facing API responses.',
    emailPlaceholderSignIn: 'Enter your email address',
    passwordPlaceholderSignIn: 'Enter your password',
    rememberEmail: 'Remember my email',
    forgotPassword: 'Forgot password?',
    signInBtn: 'Sign In',
    signingIn: 'Signing in...',
    noAccount: "Don't have an account?",
    continueWith: 'Or continue with',
    continueWithGoogle: 'Continue with Google',
    continueWithGithub: 'Continue with GitHub',
    signUpWith: 'Or sign up with',
    signUpWithGoogle: 'Sign up with Google',
    signUpWithGithub: 'Sign up with GitHub',
    verifyHuman: 'Please complete the human verification',
    authServiceUnavailable: 'Account services are not configured for this deployment. Please contact the administrator.',
    captchaNotConfigured: 'Human verification is not configured. Please contact the administrator.',
    captchaBypassDev: 'Bypassed for local development.',
    captchaLoadFailed: 'Human verification could not load. Check your connection, then retry.',
    captchaTimedOut: 'Human verification timed out. Retry to receive a new challenge.',
    captchaUnsupported: 'This browser cannot run human verification. Use a supported browser or disable restrictive content blocking, then retry.',
    captchaRetry: 'Retry verification',
    captchaReload: 'Reload page',
    captchaRequired: 'Please complete the human verification before requesting a reset link.',
    captchaSignInError: 'Please complete the human verification before signing in.',
    forgotPasswordDesc: 'Enter your email address and we\'ll send you a password reset link.',
    sendResetLink: 'Send reset link',
    forgotPrivacyCopy: 'Your information is protected by our Privacy Policy and will only be used to send you a reset link.',
    resetEmailSent: 'Password reset email sent. Please check your inbox.',
    resetEmailSentTitle: 'Check your inbox',
    resetEmailSentDesc: 'If an account exists for that email address, we sent a password reset link. Open it to choose a new password.',
    resetRateLimit: 'Email sending limit reached. Please wait before trying again.',
    resetPasswordTitle: 'Reset password',
    resetPasswordDesc: 'Enter a new password for your AlphaLab account.',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    updatePassword: 'Update password',
    passwordUpdated: 'Password updated successfully. You can now sign in.',
    passwordUpdatedTitle: 'Password updated',
    passwordUpdatedDesc: 'Your new password is ready. Sign in again to continue to AlphaLab.',
    passwordUpdatedSignOutWarning: 'Your password was updated, but we could not confirm that every active session was signed out. Sign in and revoke other sessions from Security settings.',
    resetLinkInvalid: 'This password reset link is invalid or has expired. Please request a new link.',
    requestNewResetLink: 'Request a new reset link',
    backToSignIn: 'Back to Sign In',
    // P1-3 OAuth attribution
    oauthAttribution: 'Authentication powered by Supabase',
    // P1-4 CAPTCHA footer
    captchaFooter: 'Protected by Cloudflare Turnstile',
    // P1-5 Normalized error messages
    errorEmailExists: 'An account with this email already exists',
    errorWeakPassword: 'Password is too weak. Use at least 8 characters with an uppercase letter, a lowercase letter, and a number.',
    errorNetworkIssue: 'Network error. Please check your connection and try again.',
    errorUnexpected: 'An unexpected error occurred. Please try again.',
    errorSessionExpired: 'Your session has expired. Please sign in again.',
    // P3 Auth helper text
    signInHelperCaptcha: 'Complete verification to continue',
    signInHelperEmpty: 'Enter email and password to continue',
    signUpHelperTerms: 'Accept the terms to continue',
    signUpHelperCaptcha: 'Complete verification to continue',
    signUpHelperPasswordMismatch: 'Passwords must match',
    signUpHelperFields: 'Complete the required fields to continue',
    forgotHelperEmail: 'Enter a valid email address',
    forgotHelperCaptcha: 'Complete verification to continue',
    resetHelperPassword: 'Passwords must match and include 8+ characters, uppercase, lowercase, and a number',
    // P2 Trust indicators
    trustCloudflare: 'Cloudflare Turnstile protected',
    trustEmailReq: 'Email verification required',
    trustEncryptedConfigs: 'Encrypted Configs',
    trustSecureAuth: 'Secure Auth',
    errorResetLinkExpired: 'Reset link expired or invalid. Please request a new one.',
    recoveryTakingLong: 'Verification is taking longer than expected. This page is still waiting for the secure result and has not marked the link as failed.',
    cancelRecovery: 'Cancel and request a new link',
  },

  // Legal pages
  legal: {
    termsTitle: 'Terms of Service',
    privacyTitle: 'Privacy Policy',
    termsLastUpdated: 'Last updated: July 2026',
    privacyLastUpdated: 'Last updated: July 2026',
    backToSignUp: 'Back to Create Account',
    backToHome: 'Back to Home',
    disclaimer: 'This page is provided for transparency and does not replace professional legal advice.',
    importantNotice: 'For questions about this document or requests involving your account data, use the official AlphaLab project support channel.',
  },

  // Auth confirmed page
  authConfirmed: {
    eyebrow: 'EMAIL VERIFICATION / STATUS',
    verifying: 'Verifying your confirmation link…',
    title: 'Email confirmed',
    subtitle: 'Your account has been activated.',
    description: 'You can now sign in and configure your trading connections.',
    descriptionWithSession: 'Your email is confirmed and you are securely signed in. Continue to your workspace.',
    verificationTakingLong: 'Verification is taking longer than expected. We are still waiting for the secure result and have not marked your link as failed.',
    cancelVerification: 'Cancel and continue to Sign In',
    invalidTitle: 'Confirmation link is invalid',
    invalidDescription: 'Use the newest link from your inbox, or return to account creation to request another email.',
    expiredTitle: 'Confirmation link has expired',
    expiredDescription: 'For your security, confirmation links are time-limited. Return to account creation and request a new email.',
    networkTitle: 'We could not verify the link',
    networkDescription: 'The verification service is temporarily unavailable. Check your connection and try the link again.',
    returnToSignUp: 'Return to Account Creation',
    continueToSignIn: 'Continue to Sign In',
    backToHome: 'Back to Home',
  },
  notFound: {
    title: '404',
    subtitle: 'Page Not Found',
    description: 'This page does not exist or has moved.',
    backToHome: 'Back to Home',
    signIn: 'Sign In',
  },

  // P3 — System Status Indicator
  systemStatus: {
    online: 'Operational',
    unavailable: 'Unavailable',
    checking: 'Checking...',
    lastChecked: 'Last checked',
    apiHealthy: 'System healthy',
    unavailableDesc: 'Status check failed — API may be unreachable',
  },

  // P3 — FAQ / Trust section (used on homepage)
  faq: {
    title: 'Frequently Asked Questions',
    subtitle: 'Common questions about AlphaLab\'s platform and security practices.',
    q1: 'Does AlphaLab provide financial or investment advice?',
    a1: 'No. AlphaLab is a research, education, and decision-support platform. It provides market data, model-assisted analysis, and backtesting tools for your research. All trading decisions and execution remain your responsibility. We do not guarantee results or provide personalized financial advice.',
    q2: 'How are my API keys protected?',
    a2: 'API keys are encrypted before database storage and masked when displayed. Configured integrations receive the credentials they need on the backend. Review the deployment configuration and logs before using production credentials.',
    q3: 'What is the difference between Paper and Real trading?',
    a3: 'Paper trading simulates trades with virtual money using live market data — no real capital at risk. Real trading connects to your brokerage account and executes actual orders. You can switch modes in Settings at any time. Paper mode is ideal for strategy validation.',
    q4: 'Can the platform execute trades automatically?',
    a4: 'Only after you deliberately enable it. Manual orders require a separate review and confirmation. Automated execution is off by default; once authorized, it can act only within the broker, schedule, routing, and risk limits you configure, and you can disable it at any time.',
    q5: 'Which data providers does AlphaLab use?',
    a5: 'AlphaLab integrates with Alpaca Markets for brokerage and trading, Finnhub for market data, and Supabase for authentication and storage. Cloudflare Turnstile can protect authentication forms when its production configuration is enabled.',
    q6: 'Is AlphaLab open source?',
    a6: 'The project repository is available on GitHub for review and contribution. Check the repository license and deployment documentation before redistributing or self-hosting it.',
  },

  // P3 — Product Demo Walkthrough
  walkthrough: {
    title: 'How AlphaLab Works',
    subtitle: 'From market scan to risk-aware execution — a systematic quant workflow.',
    step1Title: 'Configure Your Stack',
    step1Desc: 'Connect your brokerage, AI providers, and market data sources. One unified configuration.',
    step2Title: 'Scan the Market',
    step2Desc: 'Run multi-factor scans across thousands of equities. Technical, momentum, and structure filters.',
    step3Title: 'Validate with AI',
    step3Desc: 'Multi-model AI reviews candidate setups. Cross-validate structure, momentum, and confluence.',
    step4Title: 'Generate Entry Plan',
    step4Desc: 'Auto-generate entry levels, stop-loss, targets, and position sizing based on your risk rules.',
    step5Title: 'Review & Execute',
    step5Desc: 'Paper-trade first. Review manual orders before submission. Optional automation remains off until you authorize it and runs only within your configured controls.',
  },

  // P3 — Security Center (public page)
  security: {
    title: 'Security & Trust',
    subtitle: 'How we protect your data and maintain platform integrity.',
    featureAuth: 'Authentication Protected',
    featureAuthDesc: 'All sensitive routes require authentication. Session management uses Supabase Auth. Production deployments should verify token storage and transmission settings.',
    featureEmail: 'Email Verification',
    featureEmailDesc: 'New accounts require email confirmation before accessing platform features, helping ensure account ownership.',
    featureTurnstile: 'CAPTCHA Protected',
    featureTurnstileDesc: 'Authentication forms use Cloudflare Turnstile when a production site key is configured. Each deployment must verify that the widget and policy are active.',
    featureCsp: 'Deployment Headers',
    featureCspDesc: 'The frontend build supports security headers including X-Content-Type-Options, Referrer-Policy, and X-Frame-Options. Production deployments should verify header configuration.',
    featureEncryption: 'Credential Storage',
    featureEncryptionDesc: 'Provider keys are handled through backend configuration, masked in the UI, and omitted from normal read responses. Production deployments should verify encryption setup.',
    featureRateLimit: 'Rate Limiting',
    featureRateLimitDesc: 'The backend is designed to support rate limiting on authentication and API endpoints. Production deployments should configure limits appropriate to their environment.',
    featureAudit: 'Audit Trail',
    featureAuditDesc: 'Key user actions and configuration changes are designed to be logged for transparency and accountability.',
    featureHeaders: 'Transport Security',
    featureHeadersDesc: 'Deployment security headers (X-Content-Type-Options, Referrer-Policy, X-Frame-Options) are included in the frontend build configuration. Verify header presence after each production deployment.',
    
    // What we never do / Security FAQ
    neverTitle: 'Common control and safety questions',
    neverSubtitle: 'These answers explain when orders can run, how credentials are handled, and how access can be revoked.',
    never1: 'Can AlphaLab submit orders automatically?',
    never1Desc: 'Manual orders require an explicit review and confirmation before submission. Automated execution is disabled by default; if you deliberately enable it, it may act only within the broker, schedule, routing, and risk limits you configure. You can disable automation or revoke broker access at any time.',
    never2: 'How are API keys protected?',
    never2Desc: 'API keys are masked in the UI and omitted from normal read responses. Verify encryption setup in your deployment, and delete or rotate credentials at any time.',
    never3: 'Can I use paper mode first?',
    never3Desc: 'Yes. AlphaLab is designed with a paper-first workflow. You can test strategies in simulation without risking live capital, before connecting a live brokerage account.',
    never4: 'How do I revoke access?',
    never4Desc: 'You have full control over your credentials. You can delete your stored API keys and revoke access from your broker or AI provider dashboards at any time.',

    ctaTitle: 'Review the controls before connecting an account.',
    ctaDesc: 'Start in paper mode, keep credentials revocable, and verify deployment controls in the environment you use.',
  },

  // Platform
  platform: {
    heroTitle: 'Research before execution',
    heroSubtitle: 'Scan US equities, review candidates, test an idea on historical data, and build a risk plan in one workspace.',
    intelTitle: 'How a candidate becomes a plan',
    intelSubtitle: 'Four linked modules narrow the market, inspect the evidence, test the idea, and define the risk before an order is reviewed.',
    phase1: 'PHASE 1',
    scanTitle: 'Market scan',
    scanDesc: 'Apply liquidity, price, momentum, and structure filters across the selected equity universe. The result is a smaller list for review.',
    phase2: 'PHASE 2',
    aiTitle: 'Evidence review',
    aiDesc: 'A configured model reviews charts and market context as a second opinion. Rule checks and historical tests remain separate.',
    phase3: 'PHASE 3',
    planTitle: 'Risk plan',
    planDesc: 'Calculate an entry zone, protective exit, targets, and position size from the account and risk limits you selected.',
    riskAware: 'Risk-Aware Decision Support',
    beyondBlind: 'Start with evidence, then decide.',
    beyondBlindDesc: 'Each candidate keeps its screening results, model review, historical test, and risk plan together for inspection.',
    check1: 'Use the configured model provider as a second review layer.',
    check2: 'Paper trading first. Manual orders require review; optional automation is disabled by default and constrained by your configured risk controls.',
    check3: 'Inputs, decisions, and rejection reasons stay attached to the research record.',
    hubTitle: 'Trading Logic Hub',
    hubPaper: 'Paper Trading: Test strategies without risking live capital.',
    hubLive: 'Live Execution: Direct broker connectivity.',
    hubAI: 'AI Provider: Configure OpenAI, DeepSeek, or Claude.',
    hubNote: '* Users bring their own API keys; secret configuration is handled securely.',
    whoTitle: 'Who is AlphaLab For?',
    whoSubtitle: 'Designed for traders who value process and precision over "gut feelings" and blind alerts.',
    traderTitle: 'Systematic Traders',
    traderDesc: 'Traders who have a defined ruleset and want to scale their execution through automation and AI-assisted filtering.',
    quantTitle: 'Quant Analysts',
    quantDesc: 'Analysts looking to integrate modern LLM reasoning into traditional quantitative screening pipelines without coding from scratch.',
    scalerTitle: 'Active Scalers',
    scalerDesc: 'Traders managing multiple setups simultaneously who need deterministic risk controls and position sizing to preserve capital.',
    readyTitle: 'Open a research workspace',
    readyDesc: 'Start in paper mode, connect the data sources you use, and review each stage before execution.',
    getStarted: 'Create account',
  },

  // Workflow
  workflow: {
    heroTitle: 'The <span style="background: linear-gradient(90deg, #60a5fa, #3b82f6); -webkit-background-clip: text; -webkit-text-fill-color: transparent;">Quant Workflow</span>, End to End',
    heroSubtitle: 'Six stages turn a broad scan into a reviewable plan. Each stage records its inputs, output, and reason to pass, hold, or reject.',
    sectionTitle: 'From scan to order review',
    sectionSubtitle: 'Select a stage to inspect its purpose, data inputs, output, and risk check.',
    step1Title: 'Market Scanner',
    step1Status: 'Global Scan',
    step1Desc: 'Apply the selected filters to the configured equity universe.',
    step1Purpose: 'Create a manageable starting set from the symbols and data sources available to the workspace.',
    step1Inputs: 'Configured price, volume, liquidity, and market-structure fields.',
    step1Outputs: 'Symbols that meet the initial price, liquidity, and volatility rules.',
    step1Risk: 'Symbols with missing data or insufficient liquidity remain excluded.',
    step2Title: 'Candidate tracking',
    step2Status: 'Monitoring',
    step2Desc: 'Keep selected candidates under the same documented rules.',
    step2Purpose: 'Update candidate evidence as new bars arrive without treating a single move as confirmation.',
    step2Inputs: 'Time-series prices, volume, and recorded structure levels.',
    step2Outputs: 'Candidates ready for the next configured screen.',
    step2Risk: 'Confirmation rules and stale-data checks remain visible.',
    step3Title: 'Fine Scan',
    step3Status: 'Refinement',
    step3Desc: 'Apply the narrower factor and structure rules.',
    step3Purpose: 'Compare candidates across the time frames and factors selected for this run.',
    step3Inputs: 'Configured time frames, volatility measures, and factor values.',
    step3Outputs: 'A smaller list for context review and historical testing.',
    step3Risk: 'Extended moves and failed structure rules are recorded as rejection reasons.',
    step4Title: 'Deeper Validation',
    step4Status: 'Context review',
    step4Desc: 'Use a configured model as a separate review layer.',
    step4Purpose: 'Review the available market context and counterevidence without replacing rule checks or tests.',
    step4Inputs: 'Technical evidence and any configured news or sector context.',
    step4Outputs: 'A review note with cited evidence, limits, and unresolved questions.',
    step4Risk: 'Model disagreement and missing sources stay visible for manual review.',
    step5Title: 'Entry Plan',
    step5Status: 'Planning',
    step5Desc: 'Calculate levels and size from the selected limits.',
    step5Purpose: 'Turn the research record into an entry, protective exit, target, and position-size proposal.',
    step5Inputs: 'Volatility measure, account value, and configured risk limits.',
    step5Outputs: 'A bounded plan ready for user review.',
    step5Risk: 'Sizing cannot exceed the limits saved for the account and plan.',
    step6Title: 'Review & execution',
    step6Status: 'Approval',
    step6Desc: 'Review the plan before paper or live routing.',
    step6Purpose: 'Keep the plan in monitoring, submit it to paper trading, or route it to a configured live broker after approval.',
    step6Inputs: 'Approved plan, selected environment, and available broker connection.',
    step6Outputs: 'An order record or a documented decision not to submit.',
    step6Risk: 'The destination, buying power, and order details are checked before submission.',
    coreWorkflow: 'CORE WORKFLOW',
    phaseLabel: 'PHASE',
    purposeLabel: 'Purpose',
    inputsLabel: 'Inputs',
    outputsLabel: 'Outputs',
    riskCheckLabel: 'Risk Check',
    readyTitle: 'Review the workflow with your own rules.',
    getStarted: 'Create account',
  },

  // Features
  features: {
    heroTitle: 'Platform Capabilities',
    heroSubtitle: 'Screen a market, review context, test a hypothesis, define risk, and decide whether a plan should move forward.',
    disclaimer: 'Built for research and decision support, not financial advice.',
    group1Title: '1. Market Scanning',
    group1Desc: 'Screen thousands of equities and retain the setups that meet the selected rules.',
    f1_1Title: 'Real-Time Scanner',
    f1_1Desc: 'Identify emerging trends and anomalies across thousands of equities with multi-factor technical screening.',
    f1_2Title: 'Fine-Grained Filters',
    f1_2Desc: 'Surgical filtering logic to detect early multi-timeframe structures like base breaks and mean reversions.',
    f1_3Title: 'Intelligent Watchlist',
    f1_3Desc: 'Monitor candidates with real-time tactical alerts and persistent technical score tracking.',
    group2Title: '2. AI Validation',
    group2Desc: 'Use a configured model as a second review layer for context and counterevidence.',
    f2_1Title: 'LLM Context Audit',
    f2_1Desc: 'Deploy specialized AI models to audit market structure context and sentiment, eliminating technical false positives.',
    f2_2Title: 'Multi-Provider Routing',
    f2_2Desc: 'Provider-agnostic architecture compatible with OpenAI, DeepSeek, Claude, and local LLM configurations.',
    f2_3Title: 'Autonomous Verification',
    f2_3Desc: 'Autonomous intelligence that orchestrates the entire research and verification workflow before you see it.',
    group3Title: '3. Backtesting & Optimization',
    group3Desc: 'Validate hypotheses against historical data before risking capital.',
    f3_1Title: 'Institutional Backtesting',
    f3_1Desc: 'Validate hypotheses against historical tick data with granular performance metrics and drawdown analysis.',
    f3_2Title: 'Parameter Optimization',
    f3_2Desc: 'Leverage quantitative algorithms to fine-tune strategy parameters for maximum alpha and minimum risk.',
    f3_3Title: 'Strategy Comparison',
    f3_3Desc: 'Side-by-side performance benchmarking of multiple quantitative models to identify the most robust setups.',
    group4Title: '4. Entry Planning & Risk Controls',
    group4Desc: 'Calculate proposed entry, exit, and size within the limits you configure.',
    f4_1Title: 'Deterministic Entry',
    f4_1Desc: 'Deterministic generation of entry zones, trailing stops, and profit targets.',
    f4_2Title: 'Position Sizing',
    f4_2Desc: 'Account-aware sizing ensuring you never risk more than 1% of equity per trade.',
    f4_3Title: 'Risk Governance',
    f4_3Desc: 'Hard-coded risk protocols that prevent over-exposure and enforce strict reward-to-risk thresholds on every plan.',
    group5Title: '5. Execution Workflow',
    group5Desc: 'Start in paper mode and require an explicit review before live routing.',
    f5_1Title: 'Paper Trading',
    f5_1Desc: 'Test new strategies and AI configurations in a simulated environment without risking live capital.',
    f5_2Title: 'Live Broker Integration',
    f5_2Desc: 'Direct execution via high-performance broker API connection (e.g., Alpaca).',
    f5_3Title: 'Execution Monitoring',
    f5_3Desc: 'Track live fills, slippage, and automated trailing stop adjustments in real-time.',
    readyTitle: 'Start with a research question.',
    readyDesc: 'Use paper mode to inspect the evidence, limits, and risk plan before execution.',
    getStarted: 'Create account',
  },

  // Technology
  technology: {
    heroTitle: 'Technology & Architecture',
    heroSubtitle: 'The workspace separates data access, model review, quantitative tests, risk planning, and broker routing so each responsibility can be inspected.',
    archTitle: 'System Architecture',
    archMarketFeeds: 'Market Feeds',
    archAIValidation: 'AI Validation Core',
    archBrokerAPI: 'Broker API',
    archDesc: 'Data collection, model review, quantitative checks, and order routing are separate parts of the system with explicit handoffs.',
    stackFrontendTitle: 'Frontend: React & TypeScript',
    stackFrontendDesc: 'React and TypeScript render the research workspace, responsive layouts, tables, charts, and forms.',
    stackBackendTitle: 'Backend: Python & Flask',
    stackBackendDesc: 'Python and Flask provide authenticated APIs for market data, research runs, settings, and trading integrations.',
    stackDataTitle: 'Market Data Hub',
    stackDataDesc: 'Configured Alpaca and Finnhub connections provide the market, company, and sector data used by the workspace.',
    stackAlpacaTitle: 'Alpaca Connectivity',
    stackAlpacaDesc: 'Direct pipeline to Alpaca Markets for paper trading and live execution routing via broker API.',
    stackLLMTitle: 'Multi-LLM Pipeline',
    stackLLMDesc: 'Provider adapters let a configured model review context and counterevidence without owning deterministic gates.',
    stackQuantTitle: 'Quant Core Engine',
    stackQuantDesc: 'Backtests and strategy comparisons record performance, costs, drawdown, and test assumptions.',
    stackValidationTitle: 'Validation Pipeline',
    stackValidationDesc: 'Stage checks keep screening rules, model notes, test results, and rejection reasons in one research record.',
    stackRiskTitle: 'Account-Aware Sizing',
    stackRiskDesc: 'Position-size proposals use the selected account and configured per-trade limits.',
    stackSecretsTitle: 'Secure Secret Management',
    stackSecretsDesc: 'Users supply provider credentials in Settings. Saved values are masked in the interface and can be rotated at the provider.',
    stackDockerTitle: 'Docker Orchestration',
    stackDockerDesc: 'Container definitions package the frontend, API, and deployment configuration for repeatable setup.',
    catClient: 'Client',
    catServer: 'Server',
    catData: 'Data Layer',
    catExecution: 'Execution',
    catIntelligence: 'Intelligence',
    catQuant: 'Quant',
    catLogic: 'Logic',
    catRisk: 'Risk',
    catSecurity: 'Security',
    catDevOps: 'DevOps',
    ctaTitle: 'Inspect the system from data to decision.',
    ctaAction: 'Create account',
  },
};

export default messages;
